import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth, db } from './firebase';
import {
  collection, doc, getDoc, getDocs, setDoc, query, where,
} from 'firebase/firestore';
import { activeCueIndex } from './captions';
import { track } from './analytics';
import { JournalEntry } from './Journal';
import { LegalModal } from './Legal';
import InstallAppButton from './InstallPrompt';

// ─── ACCESS HELPERS ───────────────────────────────────────────────────────────
function hasActiveAccess(enrollment) {
  if (!enrollment?.paid) return false;
  if (!enrollment.expiresAt) return true;
  return new Date(enrollment.expiresAt) > new Date();
}

function isExpired(enrollment) {
  return enrollment?.paid === true && enrollment?.expiresAt && new Date(enrollment.expiresAt) <= new Date();
}

function daysUntilExpiry(enrollment) {
  if (!enrollment?.expiresAt) return null;
  const diff = new Date(enrollment.expiresAt) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

// ─── PALETTE ──────────────────────────────────────────────────────────────────
const C = {
  bg: '#F2F6F7', surface: '#FFFFFF', warm: '#E3EEF0',
  brown: '#4F9CA8', dark: '#1F4E5A', text: '#1E2E33',
  muted: '#8FA6AD', accent: '#4F9CA8', green: '#3E7D6B',
  accentWarm: '#FF7A4D',
};

// Support contacts shown on the payment screens so a stuck user always knows how to
// reach a human (call or WhatsApp). Numbers are also the brand's support lines.
const HELP_CONTACTS = [
  { display: '+255 783 953 944', tel: '+255783953944', wa: '255783953944' },
  { display: '+255 755 633 711', tel: '+255755633711', wa: '255755633711' },
];

export function PaymentHelp() {
  return (
    <div style={{ marginTop: 18, paddingTop: 16, borderTop: `1px solid ${C.warm}`, textAlign: 'center' }}>
      <div style={{ color: C.dark, fontSize: 13.5, fontWeight: 600, marginBottom: 2 }}>Unahitaji msaada? Piga au tuma WhatsApp</div>
      <div style={{ color: C.muted, fontSize: 12, marginBottom: 12 }}>Need help? Call or WhatsApp us</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
        {HELP_CONTACTS.map(c => (
          <div key={c.tel} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
            <a href={`tel:${c.tel}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none', color: C.dark, border: `1px solid ${C.warm}`, borderRadius: 8, padding: '7px 12px', fontSize: 13, fontWeight: 600 }}>
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
              {c.display}
            </a>
            <a href={`https://wa.me/${c.wa}`} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none', color: '#fff', background: '#25D366', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600 }}>
              <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 0 1 8.413 3.488 11.82 11.82 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.449L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.82 9.82 0 0 0 1.523 5.276l-.999 3.648 3.965-1.271zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>
              WhatsApp
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}

// Turn the free-text `config/catalog` contacts block into tappable links: emails →
// mailto, URLs → links, phone numbers → WhatsApp (wa.me) when the line mentions
// WhatsApp, otherwise a tel: call link. Line breaks are preserved.
function linkifyContacts(text, color = '#F2F6F7') {
  const re = /([\w.+-]+@[\w-]+\.[\w.-]+)|((?:https?:\/\/|www\.)[^\s]+)|(\+?\d[\d\s().-]{7,}\d)/g;
  const lines = String(text).split('\n');
  const linkStyle = { color, textDecoration: 'underline', textUnderlineOffset: 3 };
  return lines.map((line, li) => {
    const isWa = /whats\s*app|wa\.me/i.test(line);
    const parts = [];
    let last = 0, m;
    re.lastIndex = 0;
    while ((m = re.exec(line)) !== null) {
      if (m.index > last) parts.push(line.slice(last, m.index));
      const [full, email, url, phone] = m;
      if (email) {
        parts.push(<a key={m.index} href={`mailto:${email}`} style={linkStyle}>{email}</a>);
      } else if (url) {
        const href = url.startsWith('http') ? url : `https://${url}`;
        parts.push(<a key={m.index} href={href} target="_blank" rel="noopener noreferrer" style={linkStyle}>{url}</a>);
      } else if (phone) {
        const href = isWa ? `https://wa.me/${phone.replace(/[^\d]/g, '')}` : `tel:${phone.replace(/[^\d+]/g, '')}`;
        const extra = isWa ? { target: '_blank', rel: 'noopener noreferrer' } : {};
        parts.push(<a key={m.index} href={href} {...extra} style={linkStyle}>{phone.trim()}</a>);
      }
      last = m.index + full.length;
    }
    if (last < line.length) parts.push(line.slice(last));
    return <span key={li}>{parts}{li < lines.length - 1 ? <br /> : null}</span>;
  });
}

// Manual collection for Vodacom. AzamPay still has no M-Pesa collection wallet, so
// instead of hiding the option (which sent Vodacom users to WhatsApp anyway) they send
// money directly to this number and confirm — creating a pending payment the coach
// approves with the normal "Approve payment" button.
const MANUAL_PAY = {
  provider: 'Mpesa',
  number: '0742953945',
  name: 'Rafael Shayo',
  wa: '255783953944',
};

const NETWORKS = [
  // M-Pesa (Vodacom) is collected manually (see MANUAL_PAY) — AzamPay has no M-Pesa
  // collection wallet yet. Swap `manual` for the AzamPay push once approved.
  { value: 'Mpesa',    label: 'M-Pesa',      sub: 'Vodacom',       dot: '#E60000', manual: true },
  { value: 'Tigo',     label: 'Mixx by Yas', sub: 'formerly Tigo', dot: '#00529B' },
  { value: 'Airtel',   label: 'Airtel',      sub: 'Money',         dot: '#ED1C24' },
  { value: 'Halopesa', label: 'Halo Pesa',   sub: '',              dot: '#F7941E' },
];

// Mobile-network selector. No default selection — the user must actively pick
// one (each option shows an empty radio; the chosen one fills in). Used on both
// the program and masterclass payment screens.
function NetworkPicker({ value, onChange, disabled }) {
  const border = C.border || '#D6E2E5';
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
      {NETWORKS.map(n => {
        const active = value === n.value;
        const soon = !!n.comingSoon;          // temporarily unavailable network
        const off = disabled || soon;
        return (
          <button key={n.value} type="button" onClick={() => { if (!soon) onChange(n.value); }} disabled={off}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left',
              padding: '13px 14px', borderRadius: 12, cursor: off ? 'not-allowed' : 'pointer',
              border: active ? `2px solid ${C.dark}` : `2px solid ${border}`,
              background: active ? '#E6F2F0' : (C.surface || '#fff'),
              boxShadow: active ? '0 2px 10px rgba(31,78,90,0.18)' : 'none',
              opacity: soon ? 0.5 : 1,
              fontFamily: 'Inter, sans-serif', position: 'relative', transition: 'all 0.15s', outline: 'none',
            }}>
            <span style={{ width: 16, height: 16, borderRadius: '50%', background: n.dot, flexShrink: 0, filter: soon ? 'grayscale(100%)' : 'none', boxShadow: '0 0 0 2px rgba(255,255,255,0.9), 0 0 0 3px rgba(0,0,0,0.08)' }} />
            <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
              <span style={{ fontSize: 14, fontWeight: active ? 700 : 500, color: active ? C.dark : (C.text || '#1F4E5A') }}>{n.label}</span>
              {soon
                ? <span style={{ fontSize: 11, color: C.muted || '#7A9AA0', fontStyle: 'italic' }}>Inakuja hivi karibuni</span>
                : n.manual
                  ? <span style={{ fontSize: 11, color: C.muted || '#7A9AA0' }}>Tuma kwa namba ya simu</span>
                  : n.sub && <span style={{ fontSize: 11, color: C.muted || '#7A9AA0' }}>{n.sub}</span>}
            </span>
            {!soon && (
              <span style={{
                position: 'absolute', top: 9, right: 9, width: 18, height: 18, borderRadius: '50%',
                background: active ? C.dark : 'transparent', border: active ? 'none' : `2px solid ${border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s',
              }}>
                {active && <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// Derive the 9-digit local part from any stored phone (handles +255…, 255…, 0…).
function localFromPhone(raw) {
  const digits = (raw || '').replace(/\D/g, '');
  const local = digits.startsWith('255') ? digits.slice(3) : digits.replace(/^0+/, '');
  return local.slice(0, 9);
}

// Phone entry with a fixed +255 prefix and a digits-only field (max 9, leading 0
// stripped), so users don't re-type the country code, start with 0, or mistype the
// length. The grey placeholder shows the expected format.
function PhoneField({ value, onChange, disabled }) {
  return (
    <div style={{ display: 'flex', alignItems: 'stretch', border: `1px solid ${C.brown}`, borderRadius: 10, background: C.surface, marginBottom: 20, overflow: 'hidden' }}>
      <span style={{ display: 'flex', alignItems: 'center', padding: '0 12px', color: C.text, fontSize: 15, fontWeight: 500, fontFamily: 'Inter, sans-serif', background: '#EAF1F2', borderRight: `1px solid ${C.warm}`, whiteSpace: 'nowrap' }}>+255</span>
      <input
        value={value}
        onChange={e => onChange(localFromPhone(e.target.value))}
        placeholder="712 345 678"
        inputMode="numeric"
        maxLength={9}
        disabled={disabled}
        style={{ flex: 1, minWidth: 0, padding: '12px 16px', border: 'none', outline: 'none', fontSize: 15, fontFamily: 'Inter, sans-serif', background: 'transparent', color: C.text, boxSizing: 'border-box' }}
      />
    </div>
  );
}

// ─── PRICING ──────────────────────────────────────────────────────────────────
const tzs = n => `TZS ${Number(n || 0).toLocaleString()}`;
// A program's current price, honoring an active fixed sale price.
function effectivePrice(p) {
  const base = Number(p?.price || 0);
  const sale = Number(p?.salePrice || 0);
  return (p?.discountActive && sale > 0 && sale < base) ? sale : base;
}
function isDiscounted(p) { return effectivePrice(p) < Number(p?.price || 0); }
function discountPercent(p) {
  const base = Number(p?.price || 0);
  return base ? Math.round((1 - effectivePrice(p) / base) * 100) : 0;
}

// ─── MANUAL (VODACOM) PAYMENT ─────────────────────────────────────────────────
// Shown when the user picks Vodacom/M-Pesa: send the money directly, then confirm.
// Shared by the program and masterclass payment screens.
function ManualPayPanel({ amount }) {
  const [copied, setCopied] = useState(false);
  const copyNumber = async () => {
    try {
      await navigator.clipboard.writeText(MANUAL_PAY.number);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    } catch { window.prompt('Nakili namba hii · Copy this number:', MANUAL_PAY.number); }
  };
  const steps = [
    ['Fungua M-Pesa kwenye simu yako', 'Open M-Pesa on your phone'],
    ['Chagua "Tuma pesa" (Send money)', 'Choose "Send money"'],
    [`Tuma ${tzs(amount)} kwenda namba hii hapa chini`, `Send exactly ${tzs(amount)} to the number below`],
    ['Rudi hapa na ubonyeze "Nimelipa"', 'Come back here and tap "I have paid"'],
  ];
  return (
    <div style={{ background: '#E6F2F0', border: '1px solid #BFD9DE', borderRadius: 12, padding: 16, marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#E60000', flexShrink: 0 }} />
        <span style={{ color: C.dark, fontSize: 14, fontWeight: 700 }}>M-Pesa · Vodacom</span>
      </div>

      <ol style={{ margin: '0 0 14px', paddingLeft: 20, color: C.text }}>
        {steps.map(([sw, en]) => (
          <li key={sw} style={{ fontSize: 13.5, lineHeight: 1.5, marginBottom: 6 }}>
            {sw}<br /><span style={{ color: C.muted, fontSize: 12 }}>{en}</span>
          </li>
        ))}
      </ol>

      <div style={{ background: C.surface, border: `1px solid ${C.warm}`, borderRadius: 10, padding: '12px 14px', textAlign: 'center' }}>
        <div style={{ color: C.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>Tuma kwenda · Send to</div>
        <div style={{ color: C.dark, fontSize: 22, fontWeight: 700, letterSpacing: 0.5 }}>{MANUAL_PAY.number}</div>
        <div style={{ color: C.text, fontSize: 13, marginBottom: 10 }}>{MANUAL_PAY.name}</div>
        <button type="button" onClick={copyNumber}
          style={{ background: copied ? C.green : 'transparent', color: copied ? '#fff' : C.dark, border: `1px solid ${copied ? C.green : C.brown}`, borderRadius: 8, padding: '6px 14px', fontSize: 13, cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>
          {copied ? 'Imenakiliwa ✓' : 'Nakili namba · Copy'}
        </button>
      </div>

      <p style={{ color: C.muted, fontSize: 12, lineHeight: 1.5, margin: '12px 0 0' }}>
        Baada ya kutuma, bonyeza kitufe hapa chini — taarifa zako zitatumwa kwetu WhatsApp na tutakupa ruhusa baada ya kuthibitisha malipo.
        <br /><span style={{ fontStyle: 'italic' }}>After sending, tap the button below — we'll confirm your payment and unlock your access.</span>
      </p>
    </div>
  );
}

// Record a manual (Vodacom) payment claim and build the WhatsApp handoff message.
// Writes a PENDING payments doc — the same shape the admin's existing "Approve
// payment" button consumes — plus users.paymentPending so the client also shows up
// under "Awaiting approval". No transactionRef: manual money stays out of the revenue
// total (which counts only AzamPay-confirmed payments). Returns the wa.me URL.
async function submitManualPayment({ user, userData, phone, item, kind }) {
  const uid = user.uid;
  const ref = `manual_${Date.now()}_${uid.slice(0, 8)}`;
  const isMc = kind === 'masterclass';
  const amount = isMc ? Number(item.price || 0) : effectivePrice(item);
  const payment = {
    userId: uid, phone, amount,
    method: 'manual', provider: MANUAL_PAY.provider, status: 'pending', kind,
    accessType: isMc ? 'event' : (item.accessType || 'lifetime'),
    createdAt: new Date().toISOString(),
  };
  if (isMc) payment.masterclassId = item.id; else payment.programId = item.id;

  await Promise.all([
    setDoc(doc(db, 'payments', ref), payment),
    setDoc(doc(db, 'users', uid), { paymentPending: true, phone }, { merge: true }),
  ]);

  const lines = [
    'Habari, nimelipa kwa M-Pesa · I have paid via M-Pesa',
    '',
    `Jina / Name: ${userData?.name || user.displayName || '—'}`,
    `Simu / Phone: ${phone}`,
    user.email ? `Barua pepe / Email: ${user.email}` : null,
    `${isMc ? 'Masterclass' : 'Programu / Program'}: ${item.title || item.id}`,
    `Kiasi / Amount: ${tzs(amount)}`,
    `Kumbukumbu / Ref: ${ref}`,
    '',
    'Tafadhali nipe ruhusa · Please grant my access.',
  ].filter(Boolean);
  return `https://wa.me/${MANUAL_PAY.wa}?text=${encodeURIComponent(lines.join('\n'))}`;
}

const MEDITATION_PROGRAM_ID = 'brand-new-me';

// ─── HELPERS ──────────────────────────────────────────────────────────────────
async function loadProgramContent(programId, fullAccess) {
  // Firestore rejects an unconstrained list query unless the rules grant read in a
  // document-independent way. Enrolled/admin users satisfy isEnrolledInProgram (doc-
  // independent), so they can list every session. Users without access may only read
  // preview sessions, so the query MUST be constrained to isPreview == true — else the
  // whole query is denied and they see nothing (not even previews).
  const sessionsRef = collection(db, 'programs', programId, 'sessions');
  const sessionsQuery = fullAccess ? sessionsRef : query(sessionsRef, where('isPreview', '==', true));
  const [sessionsSnap, groupsSnap, testimonialsSnap] = await Promise.all([
    getDocs(sessionsQuery),
    getDocs(collection(db, 'programs', programId, 'groups')),
    getDocs(collection(db, 'programs', programId, 'testimonials')),
  ]);
  return {
    sessions: sessionsSnap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999)),
    groups: groupsSnap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999)),
    testimonials: testimonialsSnap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999)),
  };
}

function buildDisplayList(sessions, groups) {
  const groupMap = Object.fromEntries(groups.map(g => [g.id, g]));
  const shownGroups = new Set();
  const items = [];
  for (const item of sessions) {
    if (item.groupId && !shownGroups.has(item.groupId) && groupMap[item.groupId]) {
      items.push({ type: 'group_header', group: groupMap[item.groupId] });
      shownGroups.add(item.groupId);
    }
    items.push(item);
  }
  return items;
}

function Spinner() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}>
      <div style={{ width: 40, height: 40, border: `3px solid ${C.warm}`, borderTopColor: C.brown, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// Skeleton placeholder for the program overview while the program loads — mirrors
// the real layout (cover, title, price, description, enrol card) so the page
// doesn't visibly jump when content arrives.
function ProgramOverviewSkeleton() {
  const line = (w, h = 14, mb = 10) => <div className="skel" style={{ width: w, height: h, marginBottom: mb }} />;
  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: 'Inter, sans-serif' }}>
      <div style={{ padding: '14px 24px', borderBottom: `1px solid ${C.warm}`, background: C.surface, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div className="skel" style={{ width: 100, height: 16 }} />
        <div className="skel" style={{ marginLeft: 'auto', width: 72, height: 30, borderRadius: 9 }} />
      </div>
      <div style={{ maxWidth: 700, margin: '0 auto', padding: '32px 24px' }}>
        <div className="skel" style={{ width: '100%', aspectRatio: '16 / 9', maxHeight: 380, borderRadius: 18, marginBottom: 24 }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 20 }}>
          <div className="skel" style={{ width: '55%', height: 26 }} />
          <div className="skel" style={{ width: 90, height: 24 }} />
        </div>
        {line('40%', 16, 18)}
        {line('100%')}{line('96%')}{line('90%')}{line('68%', 14, 32)}
        <div className="skel" style={{ width: '100%', height: 120, borderRadius: 16 }} />
      </div>
    </div>
  );
}

// Skeleton for the catalog while programs load — mirrors the header + card grid.
function CatalogSkeleton() {
  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: 'Inter, sans-serif' }}>
      <div style={{ background: C.dark, padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ color: '#F2F6F7', fontSize: 18, fontWeight: 500 }}>Beyond The Body</div>
        <div className="skel" style={{ width: 80, height: 30, borderRadius: 8, background: 'rgba(255,255,255,0.15)' }} />
      </div>
      <div style={{ maxWidth: 920, margin: '0 auto', padding: '40px 24px' }}>
        <div className="skel" style={{ width: 140, height: 24, marginBottom: 28 }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 24 }}>
          {[0, 1, 2, 3].map(i => (
            <div key={i} style={{ background: C.surface, borderRadius: 16, overflow: 'hidden', border: `1px solid ${C.warm}` }}>
              <div className="skel" style={{ width: '100%', height: 180, borderRadius: 0 }} />
              <div style={{ padding: 20 }}>
                <div className="skel" style={{ width: '65%', height: 18, marginBottom: 12 }} />
                <div className="skel" style={{ width: '95%', height: 12, marginBottom: 7 }} />
                <div className="skel" style={{ width: '80%', height: 12, marginBottom: 18 }} />
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div className="skel" style={{ width: 60, height: 15 }} />
                  <div className="skel" style={{ width: 70, height: 15 }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── MAIN CLIENT APP ──────────────────────────────────────────────────────────
// One-time prompt that collects a WhatsApp number from users who don't have one yet
// (Google sign-ups, email sign-ups who skipped the optional field, and existing users).
// Skippable — re-asks on the next login until a number is saved. Stored on users.phone,
// the same field phone-signups, the admin Clients view, and payment screens already use.
function WhatsAppPrompt({ user, userData, onSaved, onSkip }) {
  const [local, setLocal] = useState(() => localFromPhone(userData?.phone));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const save = async () => {
    if (local.length < 9) { setErr('Tafadhali weka namba kamili ya tarakimu 9 · Please enter a full 9-digit number'); return; }
    setSaving(true); setErr('');
    const phone = `+255${local}`;
    try {
      await setDoc(doc(db, 'users', user.uid), { phone }, { merge: true });
      onSaved(phone);
    } catch (e) { console.error('WhatsApp save failed:', e); setErr('Imeshindikana. Jaribu tena · Could not save, try again'); setSaving(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(20,50,58,0.55)', backdropFilter: 'blur(3px)', zIndex: 130, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: C.surface, borderRadius: 18, maxWidth: 380, width: '100%', padding: '28px 24px', boxShadow: '0 20px 60px rgba(20,50,58,0.3)' }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', background: C.warm, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
          <svg viewBox="0 0 16 16" width="22" height="22" fill="none" stroke={C.brown} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9.5A6 6 0 002 9.5c0 1.2.35 2.3.95 3.2L2 14l1.8-.9A6 6 0 0014 9.5z"/></svg>
        </div>
        <h2 style={{ color: C.dark, fontSize: 19, fontWeight: 600, textAlign: 'center', margin: '0 0 6px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Namba yako ya WhatsApp</h2>
        <p style={{ color: C.muted, fontSize: 13.5, textAlign: 'center', margin: '0 0 18px', lineHeight: 1.55 }}>
          Tunaitumia kukufuatilia na kukusaidia kwenye safari yako.
          <br /><span style={{ fontSize: 12 }}>We use this to check in and support you on your journey.</span>
        </p>
        <PhoneField value={local} onChange={setLocal} disabled={saving} />
        {err && <div style={{ color: '#C0392B', fontSize: 12.5, margin: '-12px 0 12px', textAlign: 'center' }}>{err}</div>}
        <button onClick={save} disabled={saving || local.length < 9}
          style={{ width: '100%', padding: 13, background: C.dark, color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: saving || local.length < 9 ? 'not-allowed' : 'pointer', opacity: saving || local.length < 9 ? 0.6 : 1, fontFamily: 'Inter, sans-serif' }}>
          {saving ? 'Inahifadhi…' : 'Hifadhi · Save'}
        </button>
        <button onClick={onSkip} disabled={saving}
          style={{ width: '100%', marginTop: 10, padding: 10, background: 'none', border: 'none', color: C.muted, fontSize: 13.5, cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>
          Baadaye · Later
        </button>
      </div>
    </div>
  );
}

export default function ClientApp({ user, onGoHome, adminPreview = false }) {
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [masterclasses, setMasterclasses] = useState([]);
  const [registrations, setRegistrations] = useState({});
  const [enrollments, setEnrollments] = useState({});
  const [catalogConfig, setCatalogConfig] = useState({});
  const [userData, setUserData] = useState({});
  const [broadcasts, setBroadcasts] = useState([]);
  const [dismissedIds, setDismissedIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`sp_dismissed_${user.uid}`) || '[]'); }
    catch { return []; }
  });
  // WhatsApp prompt: skipped only for this browser session (sessionStorage), so it
  // re-asks on the next login until the user saves a number.
  const [waSkipped, setWaSkipped] = useState(() => {
    try { return sessionStorage.getItem(`sp_wa_skipped_${user.uid}`) === '1'; } catch { return false; }
  });
  const [loading, setLoading] = useState(true);

  const dismissBroadcast = useCallback(id => {
    setDismissedIds(prev => {
      const updated = [...prev, id];
      localStorage.setItem(`sp_dismissed_${user.uid}`, JSON.stringify(updated));
      return updated;
    });
  }, [user.uid]);

  useEffect(() => {
    (async () => {
      try {
        const [configSnap, userSnap, programsSnap, enrollSnap, bcastSnap, mcSnap, regSnap] = await Promise.all([
          getDoc(doc(db, 'config', 'catalog')),
          getDoc(doc(db, 'users', user.uid)),
          getDocs(collection(db, 'programs')),
          getDocs(collection(db, 'enrollments', user.uid, 'programs')),
          getDocs(collection(db, 'broadcasts')),
          getDocs(collection(db, 'masterclasses')),
          getDocs(collection(db, 'registrations', user.uid, 'masterclasses')),
        ]);
        if (configSnap.exists()) setCatalogConfig(configSnap.data());

        setMasterclasses(mcSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(m => m.published && (!m.eventDate || new Date(m.eventDate).getTime() > Date.now()))
          .sort((a, b) => new Date(a.eventDate || 0) - new Date(b.eventDate || 0)));
        const regMap = {};
        regSnap.docs.forEach(d => { regMap[d.id] = d.data(); });
        setRegistrations(regMap);
        const ud = userSnap.exists() ? userSnap.data() : {};
        setUserData(ud);

        const list = programsSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(p => p.published && p.archived !== true)
          .sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));
        setPrograms(list);

        // Admin preview: grant full access to every program so the coach sees the
        // real paid-client experience. Otherwise use the user's actual enrollments.
        if (adminPreview) {
          const fake = {};
          list.forEach(p => { fake[p.id] = { paid: true, accessType: 'preview' }; });
          setEnrollments(fake);
        } else {
          const enrollMap = {};
          enrollSnap.docs.forEach(d => { enrollMap[d.id] = d.data(); });
          setEnrollments(enrollMap);
        }

        // Track last-seen so admin inactivity detection works (never in preview)
        if (ud.paid && !adminPreview) {
          setDoc(doc(db, 'users', user.uid), { lastSeen: new Date().toISOString() }, { merge: true }).catch(() => {});
        }

        setBroadcasts(bcastSnap.docs.map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.sentAt || '').localeCompare(a.sentAt || '')));
      } catch (e) { console.error('ClientApp load error:', e); }
      setLoading(false);
    })();
  }, [user.uid]);

  const updateEnrollment = useCallback((programId, data) => {
    setEnrollments(prev => ({ ...prev, [programId]: { ...(prev[programId] || {}), ...data } }));
  }, []);

  const updateRegistration = useCallback((mcId, data) => {
    setRegistrations(prev => ({ ...prev, [mcId]: { ...(prev[mcId] || {}), ...data } }));
  }, []);

  if (loading) return <CatalogSkeleton />;

  const visibleBroadcasts = broadcasts.filter(b => !dismissedIds.includes(b.id));

  const needsWhatsApp = !adminPreview && !waSkipped && localFromPhone(userData?.phone).length < 9;
  const skipWhatsApp = () => {
    try { sessionStorage.setItem(`sp_wa_skipped_${user.uid}`, '1'); } catch {}
    setWaSkipped(true);
  };

  return (
    <>
    {needsWhatsApp && (
      <WhatsAppPrompt user={user} userData={userData}
        onSaved={phone => setUserData(prev => ({ ...prev, phone }))}
        onSkip={skipWhatsApp} />
    )}
    <Routes>
      <Route path="programs" element={
        <CatalogView
          user={user} userData={userData}
          programs={programs} enrollments={enrollments}
          masterclasses={masterclasses} registrations={registrations}
          catalogConfig={catalogConfig}
          broadcasts={visibleBroadcasts.filter(b => b.target === 'all')}
          onDismissBroadcast={dismissBroadcast}
          onSelectProgram={p => navigate(`/programs/${p.id}`)}
          onEnterProgram={p => navigate(`/programs/${p.id}/play`)}
          onSelectMasterclass={m => navigate(`/masterclasses/${m.id}`)}
          onGoHome={onGoHome}
        />
      } />
      <Route path="programs/:programId" element={
        <ProgramRoute mode="overview"
          user={user} userData={userData} programs={programs} enrollments={enrollments}
          adminPreview={adminPreview} updateEnrollment={updateEnrollment}
          visibleBroadcasts={visibleBroadcasts} dismissBroadcast={dismissBroadcast} />
      } />
      <Route path="programs/:programId/play" element={
        <ProgramRoute mode="player"
          user={user} userData={userData} programs={programs} enrollments={enrollments}
          adminPreview={adminPreview} updateEnrollment={updateEnrollment}
          visibleBroadcasts={visibleBroadcasts} dismissBroadcast={dismissBroadcast} />
      } />
      <Route path="masterclasses/:masterclassId" element={
        <MasterclassRoute
          user={user} userData={userData} masterclasses={masterclasses}
          registrations={registrations} updateRegistration={updateRegistration} />
      } />
      <Route path="*" element={<Navigate to="/programs" replace />} />
    </Routes>
    </>
  );
}

// ─── ROUTE WRAPPERS ───────────────────────────────────────────────────────────
// Resolve a :programId from the URL against the loaded program list, then render
// the overview or the player. An unknown/unpublished id bounces back to the catalog.
function ProgramRoute({ mode, user, userData, programs, enrollments, adminPreview, updateEnrollment, visibleBroadcasts, dismissBroadcast }) {
  const { programId } = useParams();
  const navigate = useNavigate();
  const program = programs.find(p => p.id === programId);
  if (!program) return <Navigate to="/programs" replace />;
  const enrollment = enrollments[program.id] || {};
  if (mode === 'player') {
    return (
      <ProgramPlayer
        user={user} userData={userData}
        program={program} enrollment={enrollment} adminPreview={adminPreview}
        onBack={() => navigate('/programs')}
        onGoToOverview={() => navigate(`/programs/${program.id}`)}
        onUpdateEnrollment={data => updateEnrollment(program.id, data)}
        broadcasts={visibleBroadcasts.filter(b => b.target === program.id)}
        onDismissBroadcast={dismissBroadcast}
      />
    );
  }
  return (
    <ProgramOverview
      user={user} userData={userData}
      program={program} enrollment={enrollment}
      onBack={() => navigate('/programs')}
      onEnter={() => navigate(`/programs/${program.id}/play`)}
      onUpdateEnrollment={data => updateEnrollment(program.id, data)}
    />
  );
}

function MasterclassRoute({ user, userData, masterclasses, registrations, updateRegistration }) {
  const { masterclassId } = useParams();
  const navigate = useNavigate();
  const mc = masterclasses.find(m => m.id === masterclassId);
  if (!mc) return <Navigate to="/programs" replace />;
  return (
    <MasterclassDetail
      user={user} userData={userData}
      masterclass={mc} registration={registrations[mc.id] || {}}
      onBack={() => navigate('/programs')}
      onRegistered={data => updateRegistration(mc.id, data)}
    />
  );
}

// ─── CATALOG VIEW ─────────────────────────────────────────────────────────────
function BroadcastBanner({ broadcast, onDismiss }) {
  return (
    <div style={{ background: '#FFF8EE', border: `1px solid ${C.brown}`, borderRadius: 14, padding: '14px 18px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke={C.brown} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0,marginTop:2}}>
        <path d="M3 11l19-9-9 19-2-8-8-2z"/>
      </svg>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: C.brown, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 4, fontFamily: 'Helvetica,sans-serif' }}>
          Message from your coach
        </div>
        <p style={{ color: C.text, fontSize: 14, lineHeight: 1.7, margin: 0, whiteSpace: 'pre-line' }}>{broadcast.message}</p>
        <div style={{ color: C.muted, fontSize: 11, marginTop: 6 }}>
          {new Date(broadcast.sentAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
        </div>
      </div>
      <button onClick={() => onDismiss(broadcast.id)}
        style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 16, flexShrink: 0, padding: '0 0 0 4px', lineHeight: 1 }}>✕</button>
    </div>
  );
}

function CatalogView({ user, userData, programs, enrollments, masterclasses = [], registrations = {}, catalogConfig, broadcasts = [], onDismissBroadcast, onSelectProgram, onEnterProgram, onSelectMasterclass, onGoHome, onSignIn, onSignUp }) {
  const firstName = (userData?.name || user?.displayName || 'there').split(' ')[0];
  useEffect(() => { track('view_programs'); }, []); // funnel: reached the catalog
  // Pin programs the user can actually open (paid + not expired) into a "Continue"
  // section up top; everything else goes in the explore grid below.
  const activePrograms = programs.filter(p => hasActiveAccess(enrollments[p.id] || {}));
  const otherPrograms = programs.filter(p => !hasActiveAccess(enrollments[p.id] || {}));

  // Share a direct link to a specific course. Native share sheet on mobile,
  // clipboard copy (with brief "Link copied!" feedback) otherwise.
  const [copiedId, setCopiedId] = useState(null);
  const shareProgram = async (e, program) => {
    e.stopPropagation();
    const url = `${window.location.origin}/programs/${program.id}`;
    const title = program.title || 'Beyond The Body';
    try {
      if (navigator.share) { await navigator.share({ title, text: program.tagline || title, url }); return; }
    } catch { /* user cancelled native share — fall through to copy */ }
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(program.id);
      setTimeout(() => setCopiedId(c => (c === program.id ? null : c)), 2200);
    } catch { window.prompt('Copy this link:', url); }
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: 'Inter, sans-serif' }}>
      <div style={{ background: C.dark, padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ color: '#F2F6F7', fontSize: 18, fontWeight: 500 }}>Beyond The Body</div>
          <div style={{ color: C.brown, fontSize: 13 }}>{user ? `Welcome, ${firstName}` : 'Chagua programu yako · Choose your program'}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {onGoHome && (
            <button onClick={onGoHome} style={{ background: 'transparent', border: `1px solid ${C.brown}`, color: C.warm, padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
              Home
            </button>
          )}
          {user ? (
            <button onClick={() => signOut(auth)} style={{ background: 'transparent', border: `1px solid ${C.brown}`, color: C.warm, padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
              Sign out
            </button>
          ) : (
            <>
              <button onClick={onSignIn} style={{ background: 'transparent', border: `1px solid ${C.brown}`, color: C.warm, padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
                Ingia
              </button>
              <button onClick={onSignUp} style={{ background: C.brown, border: `1px solid ${C.brown}`, color: '#fff', padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                Jisajili
              </button>
            </>
          )}
        </div>
      </div>

      <div style={{ maxWidth: 920, margin: '0 auto', padding: '40px 24px' }}>
        {broadcasts.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
            {broadcasts.map(b => <BroadcastBanner key={b.id} broadcast={b} onDismiss={onDismissBroadcast} />)}
          </div>
        )}
        <div style={{ maxWidth: 440, margin: '0 auto 28px' }}><InstallAppButton /></div>
        {activePrograms.length > 0 && (
          <div style={{ marginBottom: 44 }}>
            <h2 style={{ color: C.dark, fontSize: 22, margin: '0 0 4px', fontWeight: 600 }}>Endelea ulipoishia</h2>
            <p style={{ color: C.muted, fontSize: 14, margin: '0 0 20px' }}>Continue where you left off</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {activePrograms.map(program => {
                const enrollment = enrollments[program.id] || {};
                const done = Object.keys(enrollment.progress || {}).length;
                const days = daysUntilExpiry(enrollment);
                return (
                  <div key={program.id} onClick={() => onEnterProgram(program)}
                    style={{ position: 'relative', display: 'flex', gap: 16, background: C.surface, borderRadius: 18, overflow: 'hidden', boxShadow: '0 4px 18px rgba(31,78,90,0.10)', cursor: 'pointer', border: `1px solid ${C.warm}`, alignItems: 'stretch' }}>
                    <button onClick={e => shareProgram(e, program)} aria-label="Share this program"
                      style={{ position: 'absolute', top: 12, right: 12, zIndex: 2, display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(31,78,90,0.62)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: copiedId === program.id ? '7px 12px' : '8px', borderRadius: 20, fontFamily: 'Inter, sans-serif', lineHeight: 1 }}>
                      {copiedId === program.id
                        ? <><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>Link copied!</>
                        : <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.6" y1="10.5" x2="15.4" y2="6.5"/><line x1="8.6" y1="13.5" x2="15.4" y2="17.5"/></svg>}
                    </button>
                    {program.coverImageUrl
                      ? <img src={program.coverImageUrl} alt="" style={{ width: 110, objectFit: 'cover', flexShrink: 0 }} />
                      : <div style={{ width: 110, flexShrink: 0, background: C.warm }} />}
                    <div style={{ padding: '16px 18px 16px 2px', flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <h3 style={{ color: C.dark, fontSize: 18, margin: '0 0 4px', fontWeight: 600 }}>{program.title}</h3>
                      <p style={{ color: C.brown, fontSize: 13, margin: '0 0 14px' }}>
                        {done > 0 ? `Vipindi ${done} vimekamilika` : 'Anza kipindi cha kwanza'}
                        <span style={{ color: C.muted, fontSize: 11.5 }}> ({done > 0 ? `${done} sessions done` : 'Start your first session'})</span>
                        {days !== null && days <= 30 ? <span style={{ color: C.muted }}> · Inaisha baada ya siku {days}</span> : ''}
                      </p>
                      <span style={{ alignSelf: 'flex-start', background: C.dark, color: C.bg, borderRadius: 10, padding: '11px 22px', fontSize: 15, fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
                        Endelea →<span style={{ fontWeight: 400, fontSize: 12, opacity: 0.85, marginLeft: 6 }}>Continue</span>
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <h2 style={{ color: C.dark, fontSize: 22, margin: '0 0 28px', fontWeight: 500 }}>{activePrograms.length > 0 ? 'Gundua programu zaidi' : 'Programs'}</h2>
        {programs.length === 0 && (
          <p style={{ color: C.muted, textAlign: 'center', padding: '48px 0' }}>No programs available yet.</p>
        )}
        {activePrograms.length > 0 && otherPrograms.length === 0 && programs.length > 0 && (
          <p style={{ color: C.muted, textAlign: 'center', padding: '8px 0 32px', fontSize: 14 }}>Hakuna programu nyingine kwa sasa · No other programs right now.</p>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 24 }}>
          {otherPrograms.map(program => {
            const enrollment = enrollments[program.id] || {};
            const isEnrolled = hasActiveAccess(enrollment);
            const isPending = enrollment.paymentPending && !enrollment.paid;
            const accessExpired = isExpired(enrollment);
            const days = daysUntilExpiry(enrollment);
            return (
              <div key={program.id}
                onClick={() => isEnrolled ? onEnterProgram(program) : onSelectProgram(program)}
                style={{ position: 'relative', background: C.surface, borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', cursor: 'pointer', border: `1px solid ${C.warm}` }}>
                <button onClick={e => shareProgram(e, program)} aria-label="Share this program"
                  style={{ position: 'absolute', top: 12, right: 12, zIndex: 2, display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(31,78,90,0.62)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: copiedId === program.id ? '7px 12px' : '8px', borderRadius: 20, fontFamily: 'Inter, sans-serif', lineHeight: 1 }}>
                  {copiedId === program.id
                    ? <><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>Link copied!</>
                    : <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.6" y1="10.5" x2="15.4" y2="6.5"/><line x1="8.6" y1="13.5" x2="15.4" y2="17.5"/></svg>}
                </button>
                {program.coverImageUrl
                  ? <img src={program.coverImageUrl} alt={program.title} style={{ width: '100%', height: 180, objectFit: 'cover' }} />
                  : <div style={{ width: '100%', height: 180, background: C.warm, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg viewBox="0 0 24 24" width="56" height="56" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 8C8 10 5.9 16.17 3.82 19.82"/><path d="M4 22c3.5-2 8-5.5 10-12"/>
                      </svg>
                    </div>
                }
                <div style={{ padding: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                    <h3 style={{ color: C.dark, fontSize: 17, margin: 0, fontWeight: 600 }}>{program.title}</h3>
                    {isEnrolled && <span style={{ background: C.green, color: '#fff', fontSize: 11, padding: '3px 8px', borderRadius: 20, fontFamily: 'Helvetica,sans-serif', flexShrink: 0, marginLeft: 8 }}>Enrolled</span>}
                    {accessExpired && <span style={{ background: '#C0392B', color: '#fff', fontSize: 11, padding: '3px 8px', borderRadius: 20, fontFamily: 'Helvetica,sans-serif', flexShrink: 0, marginLeft: 8 }}>Expired</span>}
                    {isPending && <span style={{ background: C.accent, color: '#fff', fontSize: 11, padding: '3px 8px', borderRadius: 20, fontFamily: 'Helvetica,sans-serif', flexShrink: 0, marginLeft: 8 }}>Pending</span>}
                  </div>
                  {program.tagline && <p style={{ color: C.brown, fontSize: 13, margin: '0 0 10px', lineHeight: 1.5 }}>{program.tagline}</p>}
                  {program.description && (
                    <p style={{ color: C.muted, fontSize: 13, margin: '0 0 16px', lineHeight: 1.6, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {program.description}
                    </p>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                      {!program.price
                        ? <span style={{ color: C.dark, fontSize: 15, fontWeight: 600 }}>Free</span>
                        : isDiscounted(program)
                          ? <>
                              <span style={{ color: C.muted, fontSize: 13, textDecoration: 'line-through' }}>{tzs(program.price)}</span>
                              <span style={{ color: C.dark, fontSize: 15, fontWeight: 600 }}>{tzs(effectivePrice(program))}</span>
                              <span style={{ background: '#C0392B', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 6, fontFamily: 'Helvetica,sans-serif' }}>-{discountPercent(program)}%</span>
                            </>
                          : <span style={{ color: C.dark, fontSize: 15, fontWeight: 600 }}>{tzs(program.price)}</span>}
                    </span>
                    <span style={{ color: C.brown, fontSize: 13, fontFamily: 'Helvetica,sans-serif' }}>
                      {isEnrolled ? (days !== null && days <= 30 ? `Expires in ${days}d →` : 'Continue →') : accessExpired ? 'Renew →' : isPending ? 'Awaiting approval' : program.enrollmentOpen === false ? 'Enrollment paused' : 'View →'}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {masterclasses.length > 0 && (
          <div style={{ marginTop: 52 }}>
            <h2 style={{ color: C.dark, fontSize: 22, margin: '0 0 6px', fontWeight: 500 }}>Upcoming Masterclasses</h2>
            <p style={{ color: C.muted, fontSize: 14, margin: '0 0 24px' }}>Live sessions you can join — reserve your spot.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {masterclasses.map(m => {
                const reg = registrations[m.id];
                const d = m.eventDate ? new Date(m.eventDate) : null;
                return (
                  <div key={m.id} onClick={() => onSelectMasterclass(m)}
                    style={{ display: 'flex', gap: 16, background: C.surface, borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', cursor: 'pointer', border: `1px solid ${C.warm}` }}>
                    {m.coverImageUrl
                      ? <img src={m.coverImageUrl} alt="" style={{ width: 120, objectFit: 'cover', flexShrink: 0 }} />
                      : <div style={{ width: 120, flexShrink: 0, background: 'linear-gradient(135deg,#2E6B5E,#1F4E5A)' }} />}
                    <div style={{ padding: '16px 16px 16px 0', flex: 1, minWidth: 0 }}>
                      {d && <div style={{ display: 'inline-block', background: C.accentWarm, color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, marginBottom: 8, fontFamily: 'Helvetica,sans-serif', letterSpacing: 0.4 }}>{d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} · {d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</div>}
                      <h3 style={{ color: C.dark, fontSize: 17, margin: '0 0 6px', fontWeight: 600 }}>{m.title}</h3>
                      {m.description && <p style={{ color: C.muted, fontSize: 13, margin: '0 0 12px', lineHeight: 1.6, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{m.description}</p>}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: C.dark, fontSize: 15, fontWeight: 600 }}>{m.price ? `TZS ${Number(m.price).toLocaleString()}` : 'Free'}</span>
                        {reg?.paid
                          ? <span style={{ background: C.green, color: '#fff', fontSize: 12, padding: '4px 12px', borderRadius: 20, fontFamily: 'Helvetica,sans-serif' }}>Registered ✓</span>
                          : <span style={{ color: C.brown, fontSize: 13, fontFamily: 'Helvetica,sans-serif' }}>{m.enrollmentOpen === false ? 'Closed' : 'Enroll →'}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {catalogConfig?.contacts && (
        <div style={{ background: C.dark, color: C.warm, padding: '32px 24px', textAlign: 'center', marginTop: 24 }}>
          <div style={{ maxWidth: 500, margin: '0 auto' }}>
            <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 16 }}>Wasiliana nasi</div>
            <p style={{ lineHeight: 1.85, fontSize: 14, margin: 0, color: '#D6E2E5' }}>{linkifyContacts(catalogConfig.contacts, '#FFFFFF')}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── PUBLIC CATALOG ───────────────────────────────────────────────────────────
// Logged-out version of the catalog. Mounted at /programs for visitors with no
// session, so the program list is reachable from the landing page without a
// sign-in wall (sign-in is still required to enrol or play). Bypasses ClientApp
// so it never depends on a logged-in user.uid, and loads only public data:
// published programs/masterclasses (queries MUST be constrained to published ==
// true — the rules deny an unconstrained list for anonymous users). config/catalog
// is signed-in-only, so its read is allowed to fail and falls back to defaults.
export function PublicCatalogPage() {
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [masterclasses, setMasterclasses] = useState([]);
  const [catalogConfig, setCatalogConfig] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [pSnap, mcSnap] = await Promise.all([
          getDocs(query(collection(db, 'programs'), where('published', '==', true))),
          getDocs(query(collection(db, 'masterclasses'), where('published', '==', true))).catch(() => null),
        ]);
        if (!active) return;
        setPrograms(pSnap.docs.map(d => ({ id: d.id, ...d.data() }))
          .filter(p => p.archived !== true)
          .sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999)));
        if (mcSnap) setMasterclasses(mcSnap.docs.map(d => ({ id: d.id, ...d.data() }))
          .filter(m => !m.eventDate || new Date(m.eventDate).getTime() > Date.now())
          .sort((a, b) => new Date(a.eventDate || 0) - new Date(b.eventDate || 0)));
      } catch (e) { console.error('Public catalog load error:', e); }
      try {
        const cSnap = await getDoc(doc(db, 'config', 'catalog'));
        if (active && cSnap.exists()) setCatalogConfig(cSnap.data());
      } catch { /* config is signed-in only — fall back to defaults */ }
      if (active) setLoading(false);
    })();
    return () => { active = false; };
  }, []);

  if (loading) return <CatalogSkeleton />;

  return (
    <CatalogView
      user={null} userData={{}}
      programs={programs} enrollments={{}}
      masterclasses={masterclasses} registrations={{}}
      catalogConfig={catalogConfig}
      broadcasts={[]} onDismissBroadcast={() => {}}
      onSelectProgram={p => navigate(`/programs/${p.id}`)}
      onEnterProgram={p => navigate(`/programs/${p.id}`)}
      onSelectMasterclass={m => navigate(`/masterclasses/${m.id}`)}
      onGoHome={() => navigate('/')}
      onSignIn={() => navigate('/signin')}
      onSignUp={() => navigate('/signin?mode=signup')}
    />
  );
}

// ─── PROGRAM OVERVIEW ─────────────────────────────────────────────────────────
// Public, standalone program page — the entry point for shared program links.
// Renders the overview for everyone (logged-out included); sign-in is required
// only to actually enrol or play. Bypasses ClientApp so it never depends on a
// logged-in user.uid.
export function PublicProgramPage({ user, adminPreview = false, isAdmin = false }) {
  const { programId } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(undefined); // undefined = loading
  const [enrollment, setEnrollment] = useState({});
  const [userData, setUserData] = useState({});

  useEffect(() => {
    let active = true;
    setProgram(undefined);
    (async () => {
      try {
        const pSnap = await getDoc(doc(db, 'programs', programId));
        if (!active) return;
        if (!pSnap.exists() || (!pSnap.data().published && !user)) { setProgram(null); return; }
        setProgram({ id: pSnap.id, ...pSnap.data() });
      } catch { if (active) setProgram(null); return; }

      if (user) {
        try {
          const [uSnap, eSnap] = await Promise.all([
            getDoc(doc(db, 'users', user.uid)).catch(() => null),
            getDoc(doc(db, 'enrollments', user.uid, 'programs', programId)).catch(() => null),
          ]);
          if (!active) return;
          if (uSnap?.exists()) setUserData(uSnap.data());
          if (adminPreview) setEnrollment({ paid: true, accessType: 'preview' });
          else if (eSnap?.exists()) setEnrollment(eSnap.data());
        } catch { /* overview still renders without enrollment data */ }
      }
    })();
    return () => { active = false; };
  }, [programId, user, adminPreview]);

  if (program === undefined) return <ProgramOverviewSkeleton />;
  if (program === null) return <Navigate to={user ? '/programs' : '/'} replace />;

  const next = encodeURIComponent(`/programs/${program.id}`);
  return (
    <ProgramOverview
      user={user} userData={userData}
      program={program} enrollment={enrollment}
      onBack={() => navigate(user && !isAdmin ? '/programs' : '/')}
      onEnter={() => navigate(user ? `/programs/${program.id}/play` : `/signin?next=${next}`)}
      onRequireAuth={() => navigate(`/signin?next=${next}&mode=signup`)}
      onUpdateEnrollment={data => setEnrollment(prev => ({ ...prev, ...data }))}
    />
  );
}

function ProgramOverview({ user, userData, program, enrollment, onBack, onEnter, onUpdateEnrollment, onRequireAuth }) {
  const loggedOut = !user;
  const [testimonials, setTestimonials] = useState([]);
  const [assessmentAnswers, setAssessmentAnswers] = useState({});
  const [aiInsight, setAiInsight] = useState('');
  const [assessmentDone, setAssessmentDone] = useState(false);
  const [loadingInsight, setLoadingInsight] = useState(false);
  const [payLocal, setPayLocal]       = useState(() => localFromPhone(userData?.phone));
  const [payProvider, setPayProvider] = useState('');
  const [payLoading, setPayLoading]   = useState(false);
  const [payPending, setPayPending]   = useState(false);
  const [manualDone, setManualDone]   = useState(false); // manual (Vodacom) claim submitted
  const [waUrl, setWaUrl]             = useState('');    // WhatsApp handoff (re-openable)
  const [payError, setPayError]       = useState('');
  const [legalModal, setLegalModal]   = useState(null);
  const [loading, setLoading]         = useState(true);
  const [shareCopied, setShareCopied] = useState(false);

  const handleShare = async () => {
    const url = `${window.location.origin}/programs/${program.id}`;
    const title = program.title || 'Beyond The Body';
    try {
      if (navigator.share) {
        await navigator.share({ title, text: program.tagline || title, url });
        return;
      }
    } catch { /* user cancelled native share — fall through to copy */ }
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2200);
    } catch { window.prompt('Copy this link:', url); }
  };

  const isEnrolled = hasActiveAccess(enrollment);
  const accessExpired = isExpired(enrollment);
  const questions = program.assessmentQuestions || [];

  // Funnel: a signed-in, not-yet-enrolled user is looking at a program's purchase
  // page — record "reached checkout" (to catch people who land here but never pay)
  // and fire the GA4 event. `reachedPaymentAt` on the user doc drives the admin
  // "Reached checkout — didn't confirm" list.
  useEffect(() => {
    if (!user || isEnrolled) return;
    track('begin_checkout', { program_id: program.id });
    setDoc(doc(db, 'users', user.uid),
      { reachedPaymentAt: new Date().toISOString(), reachedPaymentProgramId: program.id },
      { merge: true }).catch(() => {});
  }, [user, isEnrolled, program.id]);
  const hasAssessment = program.hasAssessment && questions.length > 0;

  useEffect(() => {
    Promise.all([
      getDocs(collection(db, 'programs', program.id, 'testimonials')).catch(() => null),
      hasAssessment && user ? getDoc(doc(db, 'assessments', user.uid)).catch(() => null) : Promise.resolve(null),
    ]).then(([testimSnap, assessSnap]) => {
      if (testimSnap) setTestimonials(testimSnap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999)));
      if (assessSnap?.exists() && assessSnap.data().programId === program.id) {
        setAssessmentDone(true);
      }
      setLoading(false);
    });
  }, [program.id]);

  const allAnswered = !hasAssessment || questions.every(q => {
    const a = assessmentAnswers[q.id];
    return a !== undefined && a !== '';
  });

  const handleAssessmentSubmit = async () => {
    setLoadingInsight(true);
    try {
      if (questions.length > 0) {
        const qAndA = questions.map(q => {
          const ans = assessmentAnswers[q.id];
          let ansText = q.type === 'rating' ? `${ans}/5`
            : q.type === 'multiple_choice' ? (q.options?.[ans] || ans)
            : (ans || '(no response)');
          return `Q: ${q.question}\nA: ${ansText}`;
        }).join('\n\n');

        const prompt = `You are Coach Elibariki Mkumbo. The client's name is ${userData?.name || 'friend'}.

Their wellness assessment for "${program.title}":
${qAndA}

Write exactly 2 short paragraphs (2 sentences each):
1. What you see in their results — warmly and honestly. Start with their name.
2. One powerful sentence on how "${program.title}" addresses this, then one sentence encouraging them to begin today.

Warm English only. No headings. No lists.`;

        const token = await auth.currentUser?.getIdToken();
        const res = await fetch('/.netlify/functions/aiinsights', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ prompt, maxTokens: 300 }),
        });
        const data = await res.json();
        if (data.text) setAiInsight(data.text);

        await setDoc(doc(db, 'assessments', user.uid), {
          programId: program.id, answers: assessmentAnswers,
          questions, completedAt: new Date().toISOString(),
        }, { merge: true });
      }
    } catch (e) { console.error('Assessment error:', e); }
    setLoadingInsight(false);
    setAssessmentDone(true);
  };

  const handleAzamPay = async () => {
    setPayError('');
    if (!payProvider) { setPayError('Tafadhali chagua mtandao wako wa simu.'); return; }
    if (payLocal.length !== 9) { setPayError('Tafadhali weka namba ya simu yenye tarakimu 9 (mfano 712345678).'); return; }
    setPayLoading(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/.netlify/functions/azampay-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ phone: '+255' + payLocal, programId: program.id, provider: payProvider }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Malipo yameshindikana. Tafadhali jaribu tena.');
      track('payment_confirmed', { program_id: program.id });
      setPayPending(true);
    } catch (e) {
      setPayError(e.message || 'Malipo yameshindikana. Tafadhali jaribu tena.');
    } finally {
      setPayLoading(false);
    }
  };

  // Vodacom: the user has sent the money manually — log the claim and hand off to
  // WhatsApp so the coach can verify and approve.
  const handleManualPaid = async () => {
    setPayError('');
    if (payLocal.length !== 9) { setPayError('Tafadhali weka namba ya simu yenye tarakimu 9 (mfano 712345678).'); return; }
    setPayLoading(true);
    try {
      const url = await submitManualPayment({ user, userData, phone: '+255' + payLocal, item: program, kind: 'program' });
      track('payment_confirmed', { program_id: program.id, method: 'manual' });
      setWaUrl(url);
      window.open(url, '_blank', 'noopener');
      setManualDone(true);
    } catch (e) {
      setPayError('Imeshindwa kutuma taarifa. Tafadhali jaribu tena.');
    } finally {
      setPayLoading(false);
    }
  };

  const checkPaymentStatus = async () => {
    setPayError('');
    setPayLoading(true);
    try {
      const snap = await getDoc(doc(db, 'enrollments', user.uid, 'programs', program.id));
      if (snap.exists() && snap.data().paid) {
        onUpdateEnrollment(snap.data());
        onEnter();
      } else {
        setPayError('Malipo hayajathibitishwa bado — tafadhali subiri kidogo kisha ujaribu tena.');
      }
    } catch {
      setPayError('Imeshindwa kuangalia status. Tafadhali jaribu tena.');
    } finally {
      setPayLoading(false);
    }
  };

  const showPayment = !hasAssessment || assessmentDone;
  // Vodacom is collected manually (no AzamPay M-Pesa wallet yet) — swaps the push
  // flow for send-money instructions + a WhatsApp confirmation.
  const isManual = payProvider === MANUAL_PAY.provider;

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: 'Inter, sans-serif' }}>
      <style>{`
        .po-body{max-width:700px;margin:0 auto;padding:32px 24px}
        .po-cover{width:100%;border-radius:18px;margin-bottom:24px;display:block;object-fit:cover;max-height:380px;background:${C.warm}}
        .po-titlerow{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;margin-bottom:8px}
        .po-price{text-align:right;white-space:nowrap}
        .po-sticky{position:fixed;left:0;right:0;bottom:0;z-index:1200;display:none;align-items:center;gap:12px;padding:10px 16px calc(10px + env(safe-area-inset-bottom));background:rgba(255,255,255,.97);backdrop-filter:blur(8px);border-top:1px solid ${C.warm};box-shadow:0 -4px 20px rgba(20,50,58,.1)}
        .po-sticky-price{font-size:16px;font-weight:700;color:${C.dark};white-space:nowrap}
        .po-sticky-btn{flex:1;background:${C.dark};color:${C.bg};border:none;border-radius:12px;padding:14px 0;font-size:16px;font-weight:600;cursor:pointer;font-family:'Inter',sans-serif}
        @media(max-width:768px){.po-sticky{display:flex}.po-body{padding-bottom:96px}}
        @media(max-width:480px){.po-titlerow{flex-direction:column;align-items:flex-start;gap:6px}.po-price{text-align:left}}
      `}</style>
      <div style={{ padding: '14px 24px', borderBottom: `1px solid ${C.warm}`, background: C.surface, display: 'flex', alignItems: 'center', gap: 16 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.brown, cursor: 'pointer', fontSize: 14, padding: 0, fontFamily: 'Inter, sans-serif' }}>
          ← All programs
        </button>
        <button onClick={handleShare} style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 7, background: 'none', border: `1px solid ${C.warm}`, color: C.brown, cursor: 'pointer', fontSize: 13, padding: '7px 14px', borderRadius: 9, fontFamily: 'Inter, sans-serif' }}>
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.6" y1="10.5" x2="15.4" y2="6.5"/><line x1="8.6" y1="13.5" x2="15.4" y2="17.5"/></svg>
          {shareCopied ? 'Link copied!' : 'Share'}
        </button>
      </div>

      <div className="po-body">
        {program.coverImageUrl && <img className="po-cover" src={program.coverImageUrl} alt={program.title} />}
        <div className="po-titlerow">
          <h1 style={{ color: C.dark, fontSize: 26, margin: 0, fontWeight: 500 }}>{program.title}</h1>
          {program.price > 0 && (
            <div className="po-price">
              {isDiscounted(program) && <div style={{ color: C.muted, fontSize: 14, textDecoration: 'line-through' }}>{tzs(program.price)}</div>}
              <div style={{ color: isDiscounted(program) ? '#C0392B' : C.dark, fontSize: 20, fontWeight: 600 }}>{tzs(effectivePrice(program))}</div>
              {isDiscounted(program) && <div style={{ color: '#C0392B', fontSize: 11, fontWeight: 700, fontFamily: 'Helvetica,sans-serif' }}>{discountPercent(program)}% OFF</div>}
            </div>
          )}
        </div>
        {program.tagline && <p style={{ color: C.brown, fontSize: 15, margin: '0 0 24px' }}>{program.tagline}</p>}

        {program.description && (
          <div style={{ marginBottom: 40 }}>
            <h2 style={{ color: C.dark, fontSize: 17, fontWeight: 500, margin: '0 0 12px' }}>About this program</h2>
            <p style={{ color: C.text, lineHeight: 1.9, fontSize: 15, whiteSpace: 'pre-line', margin: 0 }}>{program.description}</p>
          </div>
        )}

        {!loading && testimonials.length > 0 && (
          <div style={{ marginBottom: 40 }}>
            <h2 style={{ color: C.dark, fontSize: 17, fontWeight: 500, margin: '0 0 20px' }}>What clients say</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {testimonials.map(t => <TestimonialItem key={t.id} testimonial={t} />)}
            </div>
          </div>
        )}

        {accessExpired && (
          <div style={{ background: '#FDE8E8', border: '1px solid #C0392B', borderRadius: 12, padding: '14px 18px', marginBottom: 12, color: '#C0392B', fontSize: 14, textAlign: 'center' }}>
            Your access to this program has expired. Purchase again to continue.
          </div>
        )}
        {isEnrolled ? (
          <button onClick={onEnter} style={{ width: '100%', background: C.dark, color: C.bg, border: 'none', borderRadius: 12, padding: '16px 0', fontSize: 17, cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>
            Continue Program →
          </button>
        ) : loggedOut ? (
          <div id="enroll" style={{ background: C.warm, borderRadius: 20, padding: 28, marginTop: 8, textAlign: 'center' }}>
            <h2 style={{ color: C.dark, fontSize: 19, fontWeight: 600, margin: '0 0 4px' }}>Uko tayari kuanza?</h2>
            <p style={{ color: C.brown, fontSize: 13, fontWeight: 400, margin: '0 0 14px' }}>Ready to begin?</p>
            <p style={{ color: C.brown, fontSize: 14, lineHeight: 1.7, margin: '0 0 22px' }}>
              Fungua akaunti yako bila malipo ili ujiunge na programu hii uanze safari yako.
              <br /><span style={{ fontSize: 12.5, opacity: 0.8 }}>Create your free account to enrol in this program and start your journey.</span>
            </p>
            <button onClick={onRequireAuth} style={{ width: '100%', background: C.dark, color: C.bg, border: 'none', borderRadius: 12, padding: '15px 0', fontSize: 16, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, sans-serif', lineHeight: 1.3 }}>
              Fungua akaunti bila malipo →<br /><span style={{ fontSize: 12, fontWeight: 400, opacity: 0.85 }}>Create your free account</span>
            </button>
          </div>
        ) : (
          <>
            {hasAssessment && !assessmentDone && (
              <div style={{ marginBottom: 40 }}>
                <h2 style={{ color: C.dark, fontSize: 17, fontWeight: 500, margin: '0 0 8px' }}>Wellness Assessment</h2>
                <p style={{ color: C.brown, fontSize: 14, margin: '0 0 24px' }}>Complete this before enrolling so your coach can personalise your experience.</p>
                <DynamicAssessment questions={questions} answers={assessmentAnswers} onChange={setAssessmentAnswers} />
                <button
                  onClick={handleAssessmentSubmit}
                  disabled={!allAnswered || loadingInsight}
                  style={{ marginTop: 24, width: '100%', background: allAnswered ? C.dark : C.muted, color: C.bg, border: 'none', borderRadius: 12, padding: '14px 0', fontSize: 16, cursor: allAnswered ? 'pointer' : 'not-allowed', fontFamily: 'Inter, sans-serif' }}>
                  {loadingInsight ? 'Getting your insight…' : 'Submit Assessment'}
                </button>
              </div>
            )}

            {aiInsight && (
              <div style={{ background: C.warm, borderRadius: 16, padding: 24, marginBottom: 32 }}>
                <div style={{ color: C.dark, fontSize: 13, fontWeight: 600, marginBottom: 12, fontFamily: 'Helvetica,sans-serif', textTransform: 'uppercase', letterSpacing: 0.5 }}>From Coach Elibariki</div>
                <p style={{ color: C.text, lineHeight: 1.9, fontSize: 15, whiteSpace: 'pre-line', margin: 0 }}>{aiInsight}</p>
              </div>
            )}

            {program.enrollmentOpen === false && !isEnrolled && (
              <div style={{ background: '#F0F6F7', border: '1px solid #D6E2E5', borderRadius: 12, padding: '18px 20px', textAlign: 'center' }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: C.dark, marginBottom: 6 }}>Usajili Umesimamishwa</div>
                <p style={{ fontSize: 14, color: C.brown, margin: 0, lineHeight: 1.6 }}>
                  Programu hii kwa sasa haipokei wasajili wapya. Tafadhali angalia tena baadaye.
                </p>
              </div>
            )}
            {showPayment && program.enrollmentOpen !== false && (
              <div id="enroll" style={{ background: C.warm, borderRadius: 20, padding: 28, marginTop: 8 }}>
                {manualDone ? (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
                      <svg viewBox="0 0 24 24" width="44" height="44" fill="none" stroke={C.green} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/><polyline points="8 12.5 11 15.5 16 9.5"/>
                      </svg>
                    </div>
                    <h2 style={{ color: C.dark, fontSize: 18, fontWeight: 500, margin: '0 0 10px' }}>Asante! Tumepokea taarifa zako</h2>
                    <p style={{ color: C.brown, fontSize: 14, lineHeight: 1.7, margin: '0 0 20px' }}>
                      Tutathibitisha malipo yako na kukupa ruhusa hivi karibuni. Hakikisha umetuma ujumbe wa WhatsApp ili tuwahi kukuthibitisha.
                      <br /><span style={{ color: C.muted, fontSize: 12 }}>We'll verify your payment and unlock your access shortly — make sure the WhatsApp message was sent.</span>
                    </p>
                    {waUrl && (
                      <a href={waUrl} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', background: '#25D366', color: '#fff', borderRadius: 12, padding: '14px 0', fontSize: 16, textDecoration: 'none', fontWeight: 600, marginBottom: 12, boxSizing: 'border-box' }}>
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 0 1 8.413 3.488 11.82 11.82 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.449L.057 24z"/></svg>
                        Tuma tena kwa WhatsApp
                      </a>
                    )}
                    {payError && <p style={{ color: '#C0392B', fontSize: 13, margin: '0 0 16px' }}>{payError}</p>}
                    <button onClick={checkPaymentStatus} disabled={payLoading}
                      style={{ width: '100%', background: C.dark, color: C.bg, border: 'none', borderRadius: 12, padding: '14px 0', fontSize: 16, cursor: payLoading ? 'not-allowed' : 'pointer', fontFamily: 'Inter, sans-serif', opacity: payLoading ? 0.7 : 1 }}>
                      {payLoading ? 'Inaangalia…' : 'Angalia kama nimepewa ruhusa'}
                    </button>
                    <PaymentHelp />
                  </div>
                ) : payPending ? (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
                      <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke={C.dark} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="6" y="2" width="12" height="20" rx="3"/><line x1="11" y1="18" x2="13" y2="18"/>
                      </svg>
                    </div>
                    <h2 style={{ color: C.dark, fontSize: 18, fontWeight: 500, margin: '0 0 10px' }}>Angalia simu yako</h2>
                    <p style={{ color: C.brown, fontSize: 14, lineHeight: 1.7, margin: '0 0 24px' }}>
                      Ombi la malipo limetumwa kwa <strong>+255 {payLocal}</strong>.<br />
                      Weka PIN yako kwenye simu ili kukamilisha malipo.
                      <br /><span style={{ color: C.muted, fontSize: 12 }}>A prompt was sent to your phone — enter your PIN to complete payment.</span>
                    </p>
                    {payError && <p style={{ color: '#C0392B', fontSize: 13, margin: '0 0 16px' }}>{payError}</p>}
                    <button onClick={checkPaymentStatus} disabled={payLoading}
                      style={{ width: '100%', background: C.dark, color: C.bg, border: 'none', borderRadius: 12, padding: '14px 0', fontSize: 16, cursor: payLoading ? 'not-allowed' : 'pointer', fontFamily: 'Inter, sans-serif', opacity: payLoading ? 0.7 : 1 }}>
                      {payLoading ? 'Inaangalia…' : "Nimelipa — thibitisha muamala"}
                    </button>
                    <PaymentHelp />
                  </div>
                ) : (
                  <>
                    <h2 style={{ color: C.dark, fontSize: 17, fontWeight: 500, margin: '0 0 6px' }}>Jisajili katika programu hii</h2>
                    <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      {isDiscounted(program) && <span style={{ color: C.muted, fontSize: 16, textDecoration: 'line-through' }}>{tzs(program.price)}</span>}
                      <span style={{ color: isDiscounted(program) ? '#C0392B' : C.dark, fontSize: 22, fontWeight: 600 }}>{tzs(effectivePrice(program))}</span>
                      {isDiscounted(program) && <span style={{ background: '#C0392B', color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6, fontFamily: 'Helvetica,sans-serif' }}>{discountPercent(program)}% OFF</span>}
                    </div>
                    {!isManual && (
                      <div style={{ background: '#E6F2F0', border: '1px solid #BFD9DE', borderRadius: 12, padding: 16, marginBottom: 24 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#1F4E5A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/></svg>
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#1F4E5A', fontFamily: 'Helvetica,sans-serif', letterSpacing: 0.3 }}>Malipo kwa simu · Mobile Money</span>
                        </div>
                        <p style={{ fontSize: 13, color: '#4F9CA8', lineHeight: 1.6, margin: 0 }}>
                          Chagua mtandao wako na uweke namba ya simu. Utapokea ombi la malipo — weka PIN yako kuthibitisha.
                          <br /><span style={{ color: C.muted }}>Select your network and enter your phone — you'll get a prompt to enter your PIN.</span>
                        </p>
                      </div>
                    )}
                    <label style={{ color: C.dark, fontSize: 14, display: 'block', marginBottom: 8 }}>Mtandao wa simu</label>
                    <NetworkPicker value={payProvider} onChange={setPayProvider} disabled={payLoading} />
                    {isManual && <ManualPayPanel amount={effectivePrice(program)} />}
                    <label style={{ color: C.dark, fontSize: 14, display: 'block', marginBottom: 6 }}>
                      {isManual ? 'Namba ya simu uliyotumia kulipa' : 'Namba ya simu'}
                    </label>
                    <PhoneField value={payLocal} onChange={setPayLocal} disabled={payLoading} />
                    {payError && (
                      <p style={{ color: '#C0392B', fontSize: 13, margin: '0 0 16px' }}>{payError}</p>
                    )}
                    <p style={{ fontSize: 12, color: C.muted, textAlign: 'center', margin: '0 0 12px', lineHeight: 1.6 }}>
                      Kwa kulipa unakubaliana na{' '}
                      <button onClick={() => setLegalModal('terms')} style={{ background: 'none', border: 'none', padding: 0, color: C.dark, textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit', fontFamily: 'inherit' }}>Vigezo na Masharti</button>
                      {' '}yetu na{' '}
                      <button onClick={() => setLegalModal('privacy')} style={{ background: 'none', border: 'none', padding: 0, color: C.dark, textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit', fontFamily: 'inherit' }}>Sera ya Faragha</button>
                    </p>
                    <button onClick={isManual ? handleManualPaid : handleAzamPay} disabled={payLoading || !payProvider}
                      style={{ width: '100%', background: isManual ? C.green : C.dark, color: isManual ? '#fff' : C.bg, border: 'none', borderRadius: 12, padding: '15px 0', fontSize: 17, cursor: (payLoading || !payProvider) ? 'not-allowed' : 'pointer', fontFamily: 'Inter, sans-serif', opacity: (payLoading || !payProvider) ? 0.7 : 1 }}>
                      {payLoading
                        ? (isManual ? 'Inatuma…' : 'Malipo yanashughulikiwa…')
                        : isManual ? 'Nimelipa — tuma uthibitisho' : `Lipa ${tzs(effectivePrice(program))} kwa simu`}
                    </button>
                    <PaymentHelp />
                    {legalModal && <LegalModal defaultTab={legalModal} onClose={() => setLegalModal(null)} />}
                  </>
                )}
              </div>
            )}

            <div style={{ textAlign: 'center', marginTop: 24 }}>
              <button onClick={onEnter}
                style={{ background: 'none', border: `1px solid ${C.brown}`, color: C.brown, borderRadius: 10, padding: '11px 28px', fontSize: 14, cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>
                Tazama vipindi vya bure →
              </button>
            </div>
          </>
        )}
        <div style={{ maxWidth: 440, margin: '32px auto 8px' }}><InstallAppButton /></div>
      </div>

      {/* Sticky mobile enroll bar — keeps the action one tap away while scrolling */}
      {!payPending && !manualDone && (isEnrolled || loggedOut || program.enrollmentOpen !== false) && (
        <div className="po-sticky">
          {program.price > 0 && !isEnrolled && (
            <span className="po-sticky-price">{tzs(effectivePrice(program))}</span>
          )}
          <button className="po-sticky-btn"
            onClick={isEnrolled ? onEnter : loggedOut ? onRequireAuth : () => {
              const el = document.getElementById('enroll');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}>
            {isEnrolled ? 'Continue →' : loggedOut ? 'Sign in to enroll →' : 'Enroll →'}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── TESTIMONIAL ITEM ─────────────────────────────────────────────────────────
function TestimonialItem({ testimonial }) {
  const { type, url, caption, authorName } = testimonial;
  return (
    <div style={{ background: C.surface, borderRadius: 16, padding: 20, border: `1px solid ${C.warm}` }}>
      {type === 'photo' && url && <img src={url} alt={authorName || ''} style={{ width: '100%', borderRadius: 12, marginBottom: 12, objectFit: 'cover', maxHeight: 280 }} />}
      {type === 'audio' && url && <audio controls src={url} style={{ width: '100%', marginBottom: 12 }} />}
      {type === 'video' && url && <video controls src={url} style={{ width: '100%', borderRadius: 12, marginBottom: 12 }} />}
      {caption && <p style={{ color: C.text, fontSize: 14, lineHeight: 1.7, margin: '0 0 8px', fontStyle: 'italic' }}>"{caption}"</p>}
      {authorName && <div style={{ color: C.brown, fontSize: 13, fontFamily: 'Helvetica,sans-serif' }}>— {authorName}</div>}
    </div>
  );
}

// ─── DYNAMIC ASSESSMENT ───────────────────────────────────────────────────────
function DynamicAssessment({ questions, answers, onChange }) {
  const set = (id, val) => onChange(prev => ({ ...prev, [id]: val }));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
      {questions.map((q, idx) => (
        <div key={q.id}>
          <div style={{ color: C.dark, fontSize: 15, fontWeight: 500, marginBottom: 14 }}>{idx + 1}. {q.question}</div>
          {q.type === 'rating' && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {[1, 2, 3, 4, 5].map(n => (
                <button key={n} onClick={() => set(q.id, n)}
                  style={{ width: 44, height: 44, borderRadius: '50%', border: `2px solid ${answers[q.id] === n ? C.dark : C.warm}`, background: answers[q.id] === n ? C.dark : C.surface, color: answers[q.id] === n ? C.bg : C.text, cursor: 'pointer', fontSize: 15, fontFamily: 'Helvetica,sans-serif' }}>
                  {n}
                </button>
              ))}
              <span style={{ color: C.muted, fontSize: 12, marginLeft: 4 }}>1 = Not at all · 5 = Very much</span>
            </div>
          )}
          {q.type === 'multiple_choice' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(q.options || []).map((opt, i) => (
                <button key={i} onClick={() => set(q.id, i)}
                  style={{ textAlign: 'left', padding: '12px 16px', borderRadius: 10, border: `2px solid ${answers[q.id] === i ? C.dark : C.warm}`, background: answers[q.id] === i ? C.warm : C.surface, color: C.text, cursor: 'pointer', fontSize: 14, fontFamily: 'Inter, sans-serif' }}>
                  {opt}
                </button>
              ))}
            </div>
          )}
          {q.type === 'text' && (
            <textarea value={answers[q.id] || ''} onChange={e => set(q.id, e.target.value)}
              placeholder="Type your response…" rows={3}
              style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.warm}`, fontSize: 14, fontFamily: 'Inter, sans-serif', color: C.text, background: C.surface, boxSizing: 'border-box', resize: 'vertical' }} />
          )}
        </div>
      ))}
    </div>
  );
}

// ─── PROGRAM PLAYER ───────────────────────────────────────────────────────────
function ProgramPlayer({ user, userData, program, enrollment, adminPreview = false, onBack, onGoToOverview, onUpdateEnrollment, broadcasts = [], onDismissBroadcast }) {
  const [sessions, setSessions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [progress, setProgress] = useState(enrollment.progress || {});
  const [activeSession, setActiveSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coachPrograms, setCoachPrograms] = useState([]);
  const [showReassessment, setShowReassessment] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  const handleShare = async () => {
    const url = `${window.location.origin}/programs/${program.id}`;
    const title = program.title || 'Beyond The Body';
    try {
      if (navigator.share) { await navigator.share({ title, text: program.tagline || title, url }); return; }
    } catch { /* user cancelled native share — fall through to copy */ }
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2200);
    } catch { window.prompt('Copy this link:', url); }
  };

  useEffect(() => {
    (async () => {
      const [{ sessions: s, groups: g }, cpSnap] = await Promise.all([
        loadProgramContent(program.id, hasActiveAccess(enrollment)),
        getDocs(collection(db, 'coachPrograms')),
      ]);
      setSessions(s);
      setGroups(g);
      setCoachPrograms(cpSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => p.url));

      // Schedule reminders only for Meditation program (never during admin preview)
      if (program.id === MEDITATION_PROGRAM_ID && !enrollment.remindersScheduled && !adminPreview) {
        const startDate = enrollment.startDate || new Date().toISOString().split('T')[0];
        const name = userData?.name || user.displayName || '';
        const token = await auth.currentUser?.getIdToken();
        fetch('/.netlify/functions/schedule-reminders', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ email: user.email, name, startDate, programId: program.id }),
        }).then(() => {
          setDoc(doc(db, 'enrollments', user.uid, 'programs', program.id), { remindersScheduled: true, startDate }, { merge: true });
          onUpdateEnrollment({ remindersScheduled: true });
        }).catch(console.error);
      }

      setLoading(false);
    })();
  }, [program.id]);

  const markComplete = async sessionId => {
    const newProgress = { ...progress, [sessionId]: { completedAt: new Date().toISOString() } };
    setProgress(newProgress);
    if (adminPreview) return; // preview is read-only — don't persist progress
    await setDoc(doc(db, 'enrollments', user.uid, 'programs', program.id), { progress: newProgress }, { merge: true });
    onUpdateEnrollment({ progress: newProgress });
  };

  const displayItems = buildDisplayList(sessions, groups);
  // Coursework only — contemplations and interactive tools are extras and must not
  // affect progress counts or sequential unlocking.
  const sessionItems = sessions.filter(s => s.type !== 'reflection' && s.type !== 'interactive');
  const completedCount = sessionItems.filter(s => progress[s.id]).length;
  const schedule = program.unlockSchedule || 'sequential';

  const isLocked = (session, sessionIdx) => {
    if (adminPreview) return false;         // admin preview: everything unlocked
    if (progress[session.id]) return false; // always replayable
    if (session.isPreview) return false;    // preview sessions always open
    if (!hasActiveAccess(enrollment)) return true; // must purchase / renew for non-preview

    if (schedule === 'sequential') {
      if (sessionIdx === 0) return false;
      return !progress[sessionItems[sessionIdx - 1]?.id];
    }
    // Date-based schedules anchor on startDate. If it was never set (e.g. admin-granted
    // enrollments), fall back to enrolledAt/paidAt so the gate fails closed, not open.
    const startDate = enrollment.startDate || enrollment.enrolledAt || enrollment.paidAt;
    if (!startDate) return false;
    const releaseDay = session.releaseDay ?? sessionIdx;
    if (releaseDay === 0) return false;
    const unlock = new Date(startDate);
    unlock.setHours(0, 0, 0, 0);
    unlock.setDate(unlock.getDate() + releaseDay);
    if (schedule === 'timed') {
      const h = session.timeSlot === 'afternoon' ? 12 : session.timeSlot === 'evening' ? 18 : 6;
      unlock.setHours(h, 0, 0, 0);
    }
    return new Date() < unlock;
  };

  const unlockLabel = (session, sessionIdx) => {
    if (isExpired(enrollment)) return 'Muda wako umeisha — lipia tena kuendelea · Access expired — renew to continue';
    if (!hasActiveAccess(enrollment)) return 'Nunua programu hii kuifungua · Purchase this program to unlock';
    if (schedule === 'sequential') return 'Maliza kipindi kilichotangulia kufungua · Complete the previous session to unlock';
    const startDate = enrollment.startDate || enrollment.enrolledAt || enrollment.paidAt;
    if (!startDate) return 'Bado haijapatikana · Not yet available';
    const releaseDay = session.releaseDay ?? sessionIdx;
    if (releaseDay === 0) return 'Inapatikana sasa · Available now';
    const unlock = new Date(startDate);
    unlock.setHours(0, 0, 0, 0);
    unlock.setDate(unlock.getDate() + releaseDay);
    if (schedule === 'timed') {
      const h = session.timeSlot === 'afternoon' ? 12 : session.timeSlot === 'evening' ? 18 : 6;
      unlock.setHours(h, 0, 0, 0);
      const label = h === 6 ? '6:00 AM' : h === 12 ? '12:00 PM' : '6:00 PM';
      const now = new Date();
      const isToday = unlock.toDateString() === now.toDateString();
      const isTomorrow = unlock.toDateString() === new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).toDateString();
      if (isToday) return `Itapatikana leo saa ${label} · Available today at ${label}`;
      if (isTomorrow) return `Itapatikana kesho saa ${label} · Available tomorrow at ${label}`;
      return `Itapatikana ${unlock.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} saa ${label} · Available ${unlock.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}`;
    }
    const now = new Date(); now.setHours(0, 0, 0, 0);
    const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1);
    if (unlock <= tomorrow) return 'Itapatikana kesho · Available tomorrow';
    return `Itapatikana ${unlock.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} · Available ${unlock.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}`;
  };

  // Drop the user straight into where they left off: once sessions load, auto-open
  // (and scroll to) the next session to do — the first incomplete, unlocked one;
  // failing that the most recent completed session, else the first. Runs once and
  // never overrides an explicit tap.
  const autoResumedRef = useRef(false);
  useEffect(() => {
    if (autoResumedRef.current || loading || sessionItems.length === 0 || activeSession) return;
    autoResumedRef.current = true;
    const next = sessionItems.find((s, i) => !progress[s.id] && !isLocked(s, i))
      || [...sessionItems].reverse().find(s => progress[s.id])
      || sessionItems[0];
    if (!next) return;
    setActiveSession(next.id);
    setTimeout(() => document.getElementById(`sess-${next.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 250);
  }, [loading, sessions]);

  if (loading) return <Spinner />;

  if (showReassessment) {
    return (
      <ReassessmentView
        user={user} userData={userData} program={program}
        onBack={() => setShowReassessment(false)}
      />
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: 'Inter, sans-serif' }}>
      <div style={{ background: C.dark, padding: '14px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 10 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.warm, cursor: 'pointer', fontSize: 14, fontFamily: 'Inter, sans-serif', padding: 0 }}>
          ← Programs
        </button>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#F2F6F7', fontSize: 15, fontWeight: 500 }}>{program.title}</div>
          <div style={{ color: C.brown, fontSize: 12 }}>{completedCount}/{sessionItems.length} sessions complete</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button onClick={handleShare} aria-label="Share this program" style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: shareCopied ? '#F2F6F7' : C.warm, cursor: 'pointer', fontSize: 13, fontFamily: 'Inter, sans-serif', padding: 0 }}>
            {shareCopied
              ? <><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>Copied!</>
              : <><svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.6" y1="10.5" x2="15.4" y2="6.5"/><line x1="8.6" y1="13.5" x2="15.4" y2="17.5"/></svg>Share</>}
          </button>
          <button onClick={() => signOut(auth)} style={{ background: 'none', border: 'none', color: C.brown, cursor: 'pointer', fontSize: 13 }}>Sign out</button>
        </div>
      </div>

      {!hasActiveAccess(enrollment) && !loading && sessions.some(s => s.isPreview) && (
        <div style={{ background: '#FFF8EE', borderBottom: `1px solid ${C.brown}`, padding: '10px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <span style={{ color: C.text, fontSize: 13 }}>You're previewing free sessions.</span>
          <button onClick={onGoToOverview} style={{ background: C.dark, color: C.bg, border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer', fontFamily: 'Inter, sans-serif', flexShrink: 0 }}>
            Purchase →
          </button>
        </div>
      )}

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 16px' }}>
        {broadcasts.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
            {broadcasts.map(b => <BroadcastBanner key={b.id} broadcast={b} onDismiss={onDismissBroadcast} />)}
          </div>
        )}

        {userData?.coachDirectMessage && (
          <div style={{ background: '#FFF8EE', border: `1px solid ${C.brown}`, borderRadius: 14, padding: '16px 20px', marginBottom: 20, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke={C.brown} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0,marginTop:2}}>
              <path d="M17 8C8 10 5.9 16.17 3.82 19.82"/><path d="M4 22c3.5-2 8-5.5 10-12"/>
            </svg>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: C.brown, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 5, fontFamily: 'Helvetica,sans-serif' }}>
                Message from your coach
              </div>
              <p style={{ color: C.text, fontSize: 14, lineHeight: 1.75, margin: 0, whiteSpace: 'pre-line' }}>
                {userData.coachDirectMessage}
              </p>
              {userData.coachDirectMessageAt && (
                <div style={{ color: C.muted, fontSize: 11, marginTop: 8 }}>
                  {new Date(userData.coachDirectMessageAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
              )}
            </div>
          </div>
        )}

        {displayItems.length === 0 && (
          <p style={{ color: C.muted, textAlign: 'center', padding: '48px 0' }}>Vipindi vinakuja hivi karibuni — tafadhali rudi tena. · Sessions coming soon — check back shortly.</p>
        )}
        {(() => {
          let sessionIdx = 0;
          let lastSessionIdx = -1;
          let lastSessionId = null;
          let lastSession = null;
          return displayItems.map(item => {
            if (item.type === 'group_header') {
              return (
                <div key={`gh-${item.group.id}`} style={{ margin: '32px 0 12px', paddingBottom: 8, borderBottom: `2px solid ${C.warm}` }}>
                  <span style={{ color: C.dark, fontSize: 16, fontWeight: 600 }}>{item.group.name}</span>
                </div>
              );
            }
            if (item.type === 'reflection') {
              // Lock reflections using the same logic as sessions
              let locked = false;
              let label = '';
              if (schedule === 'sequential') {
                locked = lastSessionId ? !progress[lastSessionId] : false;
                if (locked) label = 'Maliza kipindi kilichotangulia kufungua · Complete the previous session to unlock';
              } else {
                const refIdx = lastSessionIdx >= 0 ? lastSessionIdx : 0;
                const effectiveReleaseDay = item.releaseDay ?? (lastSession ? (lastSession.releaseDay ?? lastSessionIdx) : 0);
                const refItem = { ...item, releaseDay: effectiveReleaseDay };
                locked = isLocked(refItem, refIdx);
                if (locked) label = unlockLabel(refItem, refIdx);
              }
              if (locked) {
                return (
                  <div key={item.id} style={{ background: C.surface, borderRadius: 16, marginBottom: 12, border: `1px solid ${C.warm}`, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, opacity: 0.55 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ color: C.muted, fontSize: 15, fontWeight: 500 }}>{item.name || 'Contemplation'}</div>
                      {label && <div style={{ color: C.muted, fontSize: 12, marginTop: 3 }}>{label}</div>}
                    </div>
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke={C.muted} strokeWidth="1.5" strokeLinecap="round" style={{marginLeft:'auto',flexShrink:0}}>
                      <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
                    </svg>
                  </div>
                );
              }
              return <ReflectionBox key={item.id} session={item} userId={user.uid} programId={program.id} />;
            }
            if (item.type === 'interactive') {
              // Optional extra — deliberately does NOT consume sessionIdx, so it never
              // affects progress counts or sequential unlocking. Gated only by access.
              const open = item.isPreview || adminPreview || hasActiveAccess(enrollment);
              return <InteractiveCard key={item.id} session={item} locked={!open} />;
            }
            lastSession = item;
            lastSessionIdx = sessionIdx;
            lastSessionId = item.id;
            const idx = sessionIdx++;
            const locked = isLocked(item, idx);
            return (
              <SessionCard
                key={item.id} session={item}
                domId={`sess-${item.id}`}
                programId={program.id}
                coverUrl={program.coverImageUrl}
                isComplete={!!progress[item.id]}
                isLocked={locked}
                lockLabel={locked ? unlockLabel(item, idx) : ''}
                isActive={activeSession === item.id}
                onToggle={() => { if (!locked) setActiveSession(prev => prev === item.id ? null : item.id); }}
                onComplete={() => markComplete(item.id)}
                userId={user.uid}
              />
            );
          });
        })()}

        {sessionItems.length > 0 && completedCount === sessionItems.length && (
          <CompletionBanner
            program={program}
            coachPrograms={coachPrograms}
            onRetake={() => setShowReassessment(true)}
            userName={userData?.name || user.displayName || ''}
          />
        )}
      </div>
    </div>
  );
}

// ─── COMPLETION BANNER ────────────────────────────────────────────────────────
function CertificateView({ program, userName, onClose }) {
  const date = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#F4FAFA', borderRadius: 16, maxWidth: 640, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
        {/* Print-only styles injected inline */}
        <style>{`@media print { body * { visibility: hidden; } #cert, #cert * { visibility: visible; } #cert { position: fixed; inset: 0; } .no-print { display: none !important; } }`}</style>
        <div id="cert" style={{ padding: '48px 56px', textAlign: 'center', background: '#F4FAFA', border: '12px solid #E3EEF0', margin: 16, borderRadius: 8, position: 'relative' }}>
          <div style={{ position: 'absolute', top: 12, left: 12, right: 12, bottom: 12, border: '1px solid #9FCAD2', borderRadius: 4, pointerEvents: 'none' }} />
          <svg viewBox="0 0 44 44" width="48" height="48" fill="none" style={{ marginBottom: 16 }}>
            <circle cx="22" cy="22" r="20" stroke="#9FCAD2" strokeWidth="1.5"/>
            <circle cx="22" cy="22" r="8" stroke="#9FCAD2" strokeWidth="1.5"/>
            <circle cx="22" cy="22" r="3" fill="#9FCAD2"/>
          </svg>
          <div style={{ color: C.muted, fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12, fontFamily: 'Helvetica,sans-serif' }}>Certificate of Completion</div>
          <div style={{ color: C.muted, fontSize: 14, marginBottom: 6 }}>This certifies that</div>
          <div style={{ color: C.dark, fontSize: 28, fontWeight: 600, margin: '8px 0', fontFamily: 'Inter, sans-serif' }}>{userName || 'Client'}</div>
          <div style={{ color: C.muted, fontSize: 14, marginBottom: 6 }}>has successfully completed</div>
          <div style={{ color: C.brown, fontSize: 22, fontWeight: 600, margin: '8px 0', fontFamily: 'Inter, sans-serif' }}>{program.title}</div>
          <div style={{ color: C.muted, fontSize: 13, marginTop: 24, marginBottom: 32 }}>{date}</div>
          <div style={{ borderTop: `1px solid ${C.warm}`, paddingTop: 20, color: C.brown, fontSize: 15, fontFamily: 'Inter, sans-serif' }}>
            Coach Elibariki Mkumbo
          </div>
          <div style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>Beyond The Body</div>
        </div>
        <div className="no-print" style={{ display: 'flex', gap: 10, padding: '0 16px 16px', justifyContent: 'center' }}>
          <button onClick={() => window.print()}
            style={{ background: C.dark, color: '#fff', border: 'none', borderRadius: 10, padding: '11px 24px', cursor: 'pointer', fontSize: 14, fontFamily: 'Inter, sans-serif' }}>
            Print / Save PDF
          </button>
          <button onClick={onClose}
            style={{ background: 'none', border: `1px solid ${C.warm}`, color: C.muted, borderRadius: 10, padding: '11px 24px', cursor: 'pointer', fontSize: 14, fontFamily: 'Inter, sans-serif' }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function CompletionBanner({ program, coachPrograms, onRetake, userName }) {
  const [showCert, setShowCert] = useState(false);
  return (
    <div style={{ marginTop: 32, borderRadius: 20, overflow: 'hidden', border: `1px solid #B8D4B0` }}>
      {/* Hero */}
      <div style={{ background: 'linear-gradient(135deg, #2E6B5E 0%, #3E7D6B 100%)', padding: '40px 32px', textAlign: 'center' }}>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center' }}>
          <svg viewBox="0 0 24 24" width="52" height="52" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 8C8 10 5.9 16.17 3.82 19.82"/><path d="M4 22c3.5-2 8-5.5 10-12"/>
          </svg>
        </div>
        <h2 style={{ color: '#F2F6F7', fontSize: 24, fontWeight: 500, margin: '0 0 12px', fontFamily: 'Inter, sans-serif' }}>
          You did it!
        </h2>
        <p style={{ color: '#C8DAC6', fontSize: 15, lineHeight: 1.75, margin: '0 auto', maxWidth: 440 }}>
          Congratulations on completing <strong style={{ color: '#F2F6F7' }}>{program.title}</strong>.
          You have taken a meaningful step in your journey — that takes real courage and commitment.
        </p>
      </div>

      {/* Body */}
      <div style={{ background: '#EDF2EB', padding: '28px 32px', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Reassessment */}
        {program.hasAssessment && (
          <div style={{ background: '#fff', borderRadius: 14, padding: '20px 24px', border: '1px solid #B8D4B0' }}>
            <div style={{ color: '#2E6B5E', fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8, fontFamily: 'Helvetica,sans-serif' }}>
              Track Your Growth
            </div>
            <p style={{ color: C.text, fontSize: 14, lineHeight: 1.7, margin: '0 0 16px' }}>
              Retake the wellness assessment to see how you've grown since you started this program.
            </p>
            <button onClick={onRetake}
              style={{ background: '#3E7D6B', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 24px', fontSize: 14, fontFamily: 'Inter, sans-serif', cursor: 'pointer' }}>
              Retake Wellness Assessment →
            </button>
          </div>
        )}

        {/* Other programs */}
        {coachPrograms.length > 0 && (
          <div>
            <div style={{ color: C.dark, fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Continue Your Journey</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {coachPrograms.map(p => (
                <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer"
                  style={{ background: '#fff', borderRadius: 12, padding: '14px 18px', border: `1px solid ${C.warm}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', textDecoration: 'none', color: C.dark }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 2 }}>{p.name}</div>
                    {p.description && <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>{p.description}</div>}
                  </div>
                  <span style={{ color: C.brown, fontSize: 18, flexShrink: 0, marginLeft: 12 }}>→</span>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Certificate */}
        <div style={{ background: '#fff', borderRadius: 14, padding: '20px 24px', border: `1px solid ${C.warm}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ color: C.dark, fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Your Certificate of Completion</div>
            <div style={{ color: C.muted, fontSize: 13 }}>Download or print your personalised certificate.</div>
          </div>
          <button onClick={() => setShowCert(true)}
            style={{ background: C.brown, color: '#fff', border: 'none', borderRadius: 10, padding: '10px 20px', fontSize: 14, fontFamily: 'Inter, sans-serif', cursor: 'pointer', flexShrink: 0 }}>
            View Certificate →
          </button>
        </div>

        <p style={{ color: C.muted, fontSize: 13, textAlign: 'center', margin: 0, lineHeight: 1.6 }}>
          Your sessions remain available to replay anytime.
        </p>
      </div>
      {showCert && <CertificateView program={program} userName={userName} onClose={() => setShowCert(false)} />}
    </div>
  );
}

// ─── REASSESSMENT VIEW ────────────────────────────────────────────────────────
function ReassessmentView({ user, userData, program, onBack }) {
  const questions = program.assessmentQuestions || [];
  const [originalData, setOriginalData] = useState(null);
  const [newAnswers, setNewAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDoc(doc(db, 'assessments', user.uid)).then(snap => {
      if (snap.exists()) setOriginalData(snap.data());
      setLoading(false);
    });
  }, [user.uid]);

  const allAnswered = questions.every(q => newAnswers[q.id] !== undefined && newAnswers[q.id] !== '');

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'assessments', user.uid), {
        previousAnswers: originalData?.answers || {},
        previousCompletedAt: originalData?.completedAt || null,
        answers: newAnswers, questions,
        programId: program.id, isReassessment: true,
        completedAt: new Date().toISOString(),
      }, { merge: true });
    } catch (e) { console.error(e); }
    setSaving(false);
    setSubmitted(true);
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: 'Inter, sans-serif' }}>
      <div style={{ background: C.dark, padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 16, position: 'sticky', top: 0, zIndex: 10 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.warm, cursor: 'pointer', fontSize: 14, fontFamily: 'Inter, sans-serif', padding: 0 }}>← Back</button>
        <div style={{ color: '#F2F6F7', fontSize: 15, fontWeight: 500 }}>Wellness Reassessment</div>
      </div>

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '32px 16px' }}>
        {!submitted ? (
          <>
            <div style={{ marginBottom: 28 }}>
              <h2 style={{ color: C.dark, fontSize: 22, fontWeight: 500, margin: '0 0 8px' }}>How are you feeling now?</h2>
              <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.7, margin: 0 }}>
                Answer the same questions honestly to see how you've grown since starting {program.title}.
              </p>
            </div>
            {questions.length === 0 ? (
              <p style={{ color: C.muted, textAlign: 'center', padding: '40px 0' }}>No assessment questions configured for this program.</p>
            ) : (
              <>
                <DynamicAssessment questions={questions} answers={newAnswers} onChange={setNewAnswers} />
                <button onClick={handleSubmit} disabled={!allAnswered || saving}
                  style={{ marginTop: 28, width: '100%', background: allAnswered ? C.dark : C.muted, color: C.bg, border: 'none', borderRadius: 12, padding: '14px 0', fontSize: 16, cursor: allAnswered ? 'pointer' : 'not-allowed', fontFamily: 'Inter, sans-serif' }}>
                  {saving ? 'Saving…' : 'See my progress →'}
                </button>
              </>
            )}
          </>
        ) : (
          <ScoreComparison
            questions={questions}
            before={originalData?.answers || {}}
            beforeDate={originalData?.completedAt}
            after={newAnswers}
            onDone={onBack}
          />
        )}
      </div>
    </div>
  );
}

function ScoreComparison({ questions, before, beforeDate, after, onDone }) {
  const ratingQs = questions.filter(q => q.type === 'rating');
  const hasRatings = ratingQs.length > 0;

  const avgBefore = hasRatings
    ? ratingQs.reduce((s, q) => s + (Number(before[q.id]) || 0), 0) / ratingQs.length
    : null;
  const avgAfter = hasRatings
    ? ratingQs.reduce((s, q) => s + (Number(after[q.id]) || 0), 0) / ratingQs.length
    : null;
  const delta = avgAfter !== null ? (avgAfter - avgBefore) : null;

  const beforeLabel = beforeDate
    ? new Date(beforeDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : 'Before';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'center' }}>
          <svg viewBox="0 0 24 24" width="44" height="44" fill="none" stroke={C.green} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 8C8 10 5.9 16.17 3.82 19.82"/><path d="M4 22c3.5-2 8-5.5 10-12"/>
          </svg>
        </div>
        <h2 style={{ color: C.dark, fontSize: 22, fontWeight: 500, margin: '0 0 8px' }}>Your Progress</h2>
        <p style={{ color: C.muted, fontSize: 14, margin: 0 }}>Here's how you've grown.</p>
      </div>

      {hasRatings && (
        <div style={{ background: C.surface, borderRadius: 16, padding: '24px', border: `1px solid ${C.warm}`, textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 40, marginBottom: 12 }}>
            <div>
              <div style={{ color: C.muted, fontSize: 12, marginBottom: 4 }}>{beforeLabel}</div>
              <div style={{ color: C.dark, fontSize: 32, fontWeight: 600 }}>{avgBefore.toFixed(1)}<span style={{ fontSize: 16, color: C.muted }}>/5</span></div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', color: C.muted, fontSize: 24 }}>→</div>
            <div>
              <div style={{ color: C.muted, fontSize: 12, marginBottom: 4 }}>Today</div>
              <div style={{ color: delta >= 0 ? C.green : '#C0392B', fontSize: 32, fontWeight: 600 }}>{avgAfter.toFixed(1)}<span style={{ fontSize: 16, color: C.muted }}>/5</span></div>
            </div>
          </div>
          {delta !== null && (
            <div style={{ fontSize: 14, color: delta > 0 ? C.green : delta < 0 ? '#C0392B' : C.muted, fontWeight: 500 }}>
              {delta > 0 ? `↑ +${delta.toFixed(1)} improvement` : delta < 0 ? `↓ ${Math.abs(delta).toFixed(1)} change` : 'No change in scores'}
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {questions.map(q => {
          const b = before[q.id];
          const a = after[q.id];
          const improved = q.type === 'rating' && Number(a) > Number(b);
          const declined = q.type === 'rating' && Number(a) < Number(b);
          return (
            <div key={q.id} style={{ background: C.surface, borderRadius: 14, padding: '16px 20px', border: `1px solid ${improved ? '#B8D4B0' : declined ? '#F5C6C6' : C.warm}` }}>
              <p style={{ color: C.dark, fontSize: 14, fontWeight: 500, margin: '0 0 12px', lineHeight: 1.5 }}>{q.question}</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div style={{ background: C.bg, borderRadius: 8, padding: '10px 14px' }}>
                  <div style={{ color: C.muted, fontSize: 11, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{beforeLabel}</div>
                  <div style={{ color: C.text, fontSize: 14 }}>
                    {q.type === 'rating' ? (b !== undefined ? `${b} / 5` : '—') : null}
                    {q.type === 'multiple_choice' ? (b !== undefined ? (q.options?.[b] ?? '—') : '—') : null}
                    {q.type === 'text' ? (b || '—') : null}
                  </div>
                </div>
                <div style={{ background: improved ? '#EDF2EB' : declined ? '#FFF0F0' : C.bg, borderRadius: 8, padding: '10px 14px', border: improved ? '1px solid #B8D4B0' : declined ? '1px solid #F5C6C6' : 'none' }}>
                  <div style={{ color: C.muted, fontSize: 11, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Today</div>
                  <div style={{ color: improved ? C.green : declined ? '#C0392B' : C.text, fontSize: 14, fontWeight: improved || declined ? 600 : 400 }}>
                    {q.type === 'rating' ? (a !== undefined ? `${a} / 5` : '—') : null}
                    {q.type === 'multiple_choice' ? (a !== undefined ? (q.options?.[a] ?? '—') : '—') : null}
                    {q.type === 'text' ? (a || '—') : null}
                  </div>
                </div>
              </div>
              {q.type === 'rating' && b !== undefined && a !== undefined && (
                <div style={{ marginTop: 10, height: 4, background: '#E3EEF0', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${(Number(a)/5)*100}%`, background: improved ? C.green : declined ? '#C0392B' : C.brown, borderRadius: 2, transition: 'width 0.8s ease' }} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button onClick={onDone}
        style={{ background: C.dark, color: C.bg, border: 'none', borderRadius: 12, padding: '14px 0', fontSize: 16, cursor: 'pointer', fontFamily: 'Inter, sans-serif', width: '100%' }}>
        Back to sessions
      </button>
    </div>
  );
}

// ─── MASTERCLASS DETAIL ───────────────────────────────────────────────────────
function MasterclassDetail({ user, userData, masterclass, registration, onBack, onRegistered }) {
  const m = masterclass;
  const [payLocal, setPayLocal] = useState(() => localFromPhone(userData?.phone));
  const [payProvider, setPayProvider] = useState('');
  const [payLoading, setPayLoading] = useState(false);
  const [payPending, setPayPending] = useState(false);
  const [manualDone, setManualDone] = useState(false);
  const [waUrl, setWaUrl] = useState('');
  const [payError, setPayError] = useState('');
  const [legalModal, setLegalModal] = useState(null);
  const [registered, setRegistered] = useState(!!registration?.paid);

  const d = m.eventDate ? new Date(m.eventDate) : null;
  const closed = m.enrollmentOpen === false || (d && d.getTime() < Date.now());
  const isManual = payProvider === MANUAL_PAY.provider;

  const handlePay = async () => {
    setPayError('');
    if (!payProvider) { setPayError('Tafadhali chagua mtandao wako wa simu.'); return; }
    if (payLocal.length !== 9) { setPayError('Tafadhali weka namba ya simu yenye tarakimu 9 (mfano 712345678).'); return; }
    setPayLoading(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/.netlify/functions/azampay-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ phone: '+255' + payLocal, provider: payProvider, kind: 'masterclass', masterclassId: m.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Malipo yameshindikana. Tafadhali jaribu tena.');
      setPayPending(true);
    } catch (e) { setPayError(e.message || 'Malipo yameshindikana. Tafadhali jaribu tena.'); }
    finally { setPayLoading(false); }
  };

  // Vodacom: money sent manually — log the claim and hand off to WhatsApp.
  const handleManualPaid = async () => {
    setPayError('');
    if (payLocal.length !== 9) { setPayError('Tafadhali weka namba ya simu yenye tarakimu 9 (mfano 712345678).'); return; }
    setPayLoading(true);
    try {
      const url = await submitManualPayment({ user, userData, phone: '+255' + payLocal, item: m, kind: 'masterclass' });
      setWaUrl(url);
      window.open(url, '_blank', 'noopener');
      setManualDone(true);
    } catch (e) { setPayError('Imeshindwa kutuma taarifa. Tafadhali jaribu tena.'); }
    finally { setPayLoading(false); }
  };

  const checkStatus = async () => {
    setPayError(''); setPayLoading(true);
    try {
      const snap = await getDoc(doc(db, 'registrations', user.uid, 'masterclasses', m.id));
      if (snap.exists() && snap.data().paid) {
        onRegistered(snap.data()); setRegistered(true); setPayPending(false);
      } else {
        setPayError('Malipo hayajathibitishwa bado — tafadhali subiri kidogo kisha ujaribu tena.');
      }
    } catch { setPayError('Imeshindwa kuangalia status. Tafadhali jaribu tena.'); }
    finally { setPayLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: 'Inter, sans-serif' }}>
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '24px 16px 60px' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.brown, cursor: 'pointer', fontSize: 14, padding: 0, marginBottom: 16, fontFamily: 'Inter, sans-serif' }}>← Back</button>

        <div style={{ borderRadius: 16, overflow: 'hidden', marginBottom: 20, background: 'linear-gradient(135deg,#2E6B5E,#1F4E5A)' }}>
          {m.coverImageUrl
            ? <img src={m.coverImageUrl} alt="" style={{ width: '100%', maxHeight: 320, objectFit: 'cover', display: 'block' }} />
            : <div style={{ height: 160 }} />}
        </div>

        {d && <div style={{ display: 'inline-block', background: C.accentWarm, color: '#fff', fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20, marginBottom: 12, fontFamily: 'Helvetica,sans-serif', letterSpacing: 0.4 }}>{d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long' })} · {d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</div>}
        <h1 style={{ color: C.dark, fontSize: 26, margin: '0 0 12px', fontWeight: 600 }}>{m.title}</h1>
        {m.description && <p style={{ color: C.brown, fontSize: 15, lineHeight: 1.7, margin: '0 0 24px', whiteSpace: 'pre-line' }}>{m.description}</p>}

        {registered ? (
          <div style={{ background: '#EAF5F0', border: '1px solid #BFE0D5', borderRadius: 16, padding: 24, textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
              <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke={C.green} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="8 12 11 15 16 9"/></svg>
            </div>
            <h2 style={{ color: C.dark, fontSize: 18, fontWeight: 600, margin: '0 0 8px' }}>Umejisajili!</h2>
            <p style={{ color: C.brown, fontSize: 14, lineHeight: 1.7, margin: 0 }}>Tumehifadhi nafasi yako{d ? ` kwa ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}` : ''}. Maelekezo ya kujiunga yatatumwa kabla ya tukio.</p>
          </div>
        ) : closed ? (
          <div style={{ background: C.warm, borderRadius: 16, padding: 20, textAlign: 'center', color: C.brown, fontSize: 14 }}>Usajili wa masterclass hii umefungwa.</div>
        ) : (
          <div style={{ background: C.warm, borderRadius: 20, padding: 28 }}>
            {manualDone ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
                  <svg viewBox="0 0 24 24" width="44" height="44" fill="none" stroke={C.green} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="8 12.5 11 15.5 16 9.5"/></svg>
                </div>
                <h2 style={{ color: C.dark, fontSize: 18, fontWeight: 500, margin: '0 0 10px' }}>Asante! Tumepokea taarifa zako</h2>
                <p style={{ color: C.brown, fontSize: 14, lineHeight: 1.7, margin: '0 0 20px' }}>
                  Tutathibitisha malipo yako na kuhifadhi nafasi yako hivi karibuni.
                  <br /><span style={{ color: C.muted, fontSize: 12 }}>We'll verify your payment and confirm your seat shortly.</span>
                </p>
                {waUrl && (
                  <a href={waUrl} target="_blank" rel="noopener noreferrer"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', background: '#25D366', color: '#fff', borderRadius: 12, padding: '14px 0', fontSize: 16, textDecoration: 'none', fontWeight: 600, marginBottom: 12, boxSizing: 'border-box' }}>
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 0 1 8.413 3.488 11.82 11.82 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.449L.057 24z"/></svg>
                    Tuma tena kwa WhatsApp
                  </a>
                )}
                {payError && <p style={{ color: '#C0392B', fontSize: 13, margin: '0 0 16px' }}>{payError}</p>}
                <button onClick={checkStatus} disabled={payLoading} style={{ width: '100%', background: C.dark, color: C.bg, border: 'none', borderRadius: 12, padding: '14px 0', fontSize: 16, cursor: payLoading ? 'not-allowed' : 'pointer', fontFamily: 'Inter, sans-serif', opacity: payLoading ? 0.7 : 1 }}>{payLoading ? 'Inaangalia…' : 'Angalia nafasi yangu'}</button>
              </div>
            ) : payPending ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
                  <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke={C.dark} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="6" y="2" width="12" height="20" rx="3"/><line x1="11" y1="18" x2="13" y2="18"/></svg>
                </div>
                <h2 style={{ color: C.dark, fontSize: 18, fontWeight: 500, margin: '0 0 10px' }}>Angalia simu yako</h2>
                <p style={{ color: C.brown, fontSize: 14, lineHeight: 1.7, margin: '0 0 24px' }}>Ombi la malipo limetumwa kwa <strong>+255 {payLocal}</strong>.<br />Weka PIN yako ili kukamilisha usajili wako.<br /><span style={{ color: C.muted, fontSize: 12 }}>A prompt was sent to your phone — enter your PIN to complete your registration.</span></p>
                {payError && <p style={{ color: '#C0392B', fontSize: 13, margin: '0 0 16px' }}>{payError}</p>}
                <button onClick={checkStatus} disabled={payLoading} style={{ width: '100%', background: C.dark, color: C.bg, border: 'none', borderRadius: 12, padding: '14px 0', fontSize: 16, cursor: payLoading ? 'not-allowed' : 'pointer', fontFamily: 'Inter, sans-serif', opacity: payLoading ? 0.7 : 1 }}>{payLoading ? 'Inaangalia…' : "Nimelipa — thibitisha nafasi yangu"}</button>
              </div>
            ) : (
              <>
                <h2 style={{ color: C.dark, fontSize: 17, fontWeight: 500, margin: '0 0 6px' }}>Hifadhi nafasi yako</h2>
                <div style={{ color: C.dark, fontSize: 22, fontWeight: 600, marginBottom: 20 }}>{m.price ? `TZS ${Number(m.price).toLocaleString()}` : 'Bure'}</div>
                <label style={{ color: C.dark, fontSize: 14, display: 'block', marginBottom: 8 }}>Mtandao wa simu</label>
                <NetworkPicker value={payProvider} onChange={setPayProvider} disabled={payLoading} />
                {isManual && <ManualPayPanel amount={Number(m.price || 0)} />}
                <label style={{ color: C.dark, fontSize: 14, display: 'block', marginBottom: 6 }}>
                  {isManual ? 'Namba ya simu uliyotumia kulipa' : 'Namba ya simu'}
                </label>
                <PhoneField value={payLocal} onChange={setPayLocal} disabled={payLoading} />
                {payError && <p style={{ color: '#C0392B', fontSize: 13, margin: '0 0 16px' }}>{payError}</p>}
                <p style={{ fontSize: 12, color: C.muted, textAlign: 'center', margin: '0 0 12px', lineHeight: 1.6 }}>
                  Kwa kulipa unakubaliana na{' '}
                  <button onClick={() => setLegalModal('terms')} style={{ background: 'none', border: 'none', padding: 0, color: C.dark, textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit', fontFamily: 'inherit' }}>Vigezo na Masharti</button>{' '}yetu na{' '}
                  <button onClick={() => setLegalModal('privacy')} style={{ background: 'none', border: 'none', padding: 0, color: C.dark, textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit', fontFamily: 'inherit' }}>Sera ya Faragha</button>
                </p>
                <button onClick={isManual ? handleManualPaid : handlePay} disabled={payLoading || !payProvider} style={{ width: '100%', background: isManual ? C.green : C.dark, color: isManual ? '#fff' : C.bg, border: 'none', borderRadius: 12, padding: '15px 0', fontSize: 17, cursor: (payLoading || !payProvider) ? 'not-allowed' : 'pointer', fontFamily: 'Inter, sans-serif', opacity: (payLoading || !payProvider) ? 0.7 : 1 }}>{payLoading ? (isManual ? 'Inatuma…' : 'Malipo yanashughulikiwa…') : isManual ? 'Nimelipa — tuma uthibitisho' : `Lipa TZS ${Number(m.price || 0).toLocaleString()} kwa simu`}</button>
                {legalModal && <LegalModal defaultTab={legalModal} onClose={() => setLegalModal(null)} />}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── SESSION CARD ─────────────────────────────────────────────────────────────
function SessionCard({ session, programId, coverUrl, isComplete, isLocked, lockLabel, isActive, onToggle, onComplete, userId, domId }) {
  const mediaRef = useRef(null);
  const seekRef = useRef(null);
  const isVideo = session.contentType === 'video';
  const name = session.name || session.title || 'Session';

  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(session.duration || 0);
  const [maxReached, setMaxReached] = useState(0);
  const [dragging, setDragging] = useState(false);

  // (H2) Media is served via a short-lived signed URL minted on demand — never a
  // permanent link. Fetch it the first time the session is opened.
  const [mediaUrl, setMediaUrl] = useState(null);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaError, setMediaError] = useState('');
  const hasMedia = !!(session.storagePath || session.audioUrl);

  // Bilingual captions — loaded on first open when the session is flagged. Only a
  // published, ready caption doc is shown. Language choice (sw/en/off) persists.
  const [caption, setCaption] = useState(null);
  const [capLang, setCapLang] = useState(() => {
    try { return localStorage.getItem('captionLang') || 'orig'; } catch { return 'orig'; }
  });
  const setLang = (l) => { setCapLang(l); try { localStorage.setItem('captionLang', l); } catch {} };

  useEffect(() => {
    if (!isActive || !hasMedia || mediaUrl || mediaLoading) return;
    let cancelled = false;
    (async () => {
      setMediaLoading(true); setMediaError('');
      try {
        const token = await auth.currentUser?.getIdToken();
        const res = await fetch('/.netlify/functions/get-media-url', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ programId, sessionId: session.id }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Could not load media');
        if (!cancelled) setMediaUrl(data.url);
      } catch (e) {
        if (!cancelled) setMediaError(e.message || 'Could not load media');
      } finally {
        if (!cancelled) setMediaLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isActive, hasMedia, programId, session.id]);

  useEffect(() => {
    if (!isActive || !session.hasCaptions || caption) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'programs', programId, 'sessions', session.id, 'captions', 'data'));
        if (!cancelled && snap.exists()) {
          const d = snap.data();
          if (d.status === 'ready' && d.published !== false && (d.cues || []).length) setCaption(d);
        }
      } catch (_) { /* captions are optional */ }
    })();
    return () => { cancelled = true; };
  }, [isActive, session.hasCaptions, programId, session.id, caption]);

  const fmt = s => `${Math.floor((s||0)/60)}:${String(Math.floor((s||0)%60)).padStart(2,'0')}`;

  // Active caption line for the current playback time (empty when off / no cue).
  // The "Original" (as-spoken) track only exists on captions generated with it; older
  // captions fall back to Swahili so the default never shows a blank track.
  const capCues = caption?.cues;
  const hasOrig = !!capCues?.some(c => c.orig && c.orig.trim());
  const effLang = (capLang === 'orig' && !hasOrig) ? 'sw' : capLang;
  const capIdx = (effLang !== 'off' && capCues) ? activeCueIndex(capCues, currentTime) : -1;
  const capText = capIdx >= 0 ? (capCues[capIdx][effLang] || '') : '';

  const togglePlay = () => {
    if (!mediaRef.current) return;
    playing ? mediaRef.current.pause() : mediaRef.current.play();
  };

  const seekTo = clientX => {
    if (!seekRef.current || !mediaRef.current || !duration) return;
    const rect = seekRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const target = Math.min(ratio * duration, maxReached);
    mediaRef.current.currentTime = target;
    setCurrentTime(target);
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = e => seekTo(e.touches ? e.touches[0].clientX : e.clientX);
    const onUp = () => setDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [dragging, maxReached, duration]);

  const mediaEvents = {
    onLoadedMetadata: () => setDuration(mediaRef.current?.duration || 0),
    onTimeUpdate: () => {
      const t = mediaRef.current?.currentTime || 0;
      setCurrentTime(t);
      setMaxReached(p => Math.max(p, t));
    },
    onPlay: () => { setPlaying(true); track('play_session', { session_id: session.id, program_id: programId }); },
    onPause: () => setPlaying(false),
    onEnded: () => { setPlaying(false); if (!isComplete) onComplete(); },
  };

  if (isLocked) {
    return (
      <div style={{ background: C.surface, borderRadius: 16, marginBottom: 12, border: `1px solid ${C.warm}`, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, opacity: 0.55 }}>
        {session.thumbnailUrl
          ? <img src={session.thumbnailUrl} alt="" style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 8, flexShrink: 0, filter: 'grayscale(60%)' }} />
          : <div style={{ width: 52, height: 52, borderRadius: 8, background: C.warm, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="1.8" strokeLinecap="round">
                <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
              </svg>
            </div>
        }
        <div style={{ minWidth: 0 }}>
          <div style={{ color: C.muted, fontSize: 15, fontWeight: 500 }}>{name}</div>
          {lockLabel && <div style={{ color: C.muted, fontSize: 12, marginTop: 3 }}>{lockLabel}</div>}
        </div>
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke={C.muted} strokeWidth="1.5" strokeLinecap="round" style={{marginLeft:'auto',flexShrink:0}}>
          <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
        </svg>
      </div>
    );
  }

  return (
    <div id={domId} style={{ background: C.surface, borderRadius: 16, marginBottom: 12, overflow: 'hidden', border: `1px solid ${isComplete ? '#B8D4B0' : C.warm}`, scrollMarginTop: 80 }}>

      {/* Header — square thumbnail + name */}
      <button onClick={onToggle} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
        {(session.thumbnailUrl || coverUrl)
          ? <img src={session.thumbnailUrl || coverUrl} alt="" style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
          : <div style={{ width: 52, height: 52, borderRadius: 8, background: isComplete ? '#D4E8CF' : C.warm, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: isComplete ? C.green : C.brown }}>
              {isComplete
                ? <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                : <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" style={{ marginLeft: 2 }}><path d="M8 5v14l11-7z"/></svg>
              }
            </div>
        }
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: C.dark, fontSize: 15, fontWeight: 500, marginBottom: 2 }}>{name}</div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            {session.duration > 0 && <span style={{ color: C.muted, fontSize: 12 }}>{fmt(session.duration)}</span>}
            {isComplete && <span style={{ color: C.green, fontSize: 12 }}>✓ Complete</span>}
            {session.isPreview && !isComplete && <span style={{ background: '#D4E8CF', color: C.green, fontSize: 11, padding: '1px 7px', borderRadius: 10, fontFamily: 'Helvetica,sans-serif' }}>Free preview</span>}
          </div>
        </div>
        <span style={{ color: C.muted, fontSize: 20, flexShrink: 0, paddingLeft: 8 }}>{isActive ? '−' : '+'}</span>
      </button>

      {/* Expanded player */}
      {isActive && (
        <div style={{ padding: '0 16px 20px' }}>
          {hasMedia ? (
            <>
              {/* Media area with play/pause overlay */}
              <div
                onClick={togglePlay}
                style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', marginBottom: 14, background: C.dark, cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', maxHeight: 'min(70vh, 560px)' }}
              >
                {isVideo
                  ? <video ref={mediaRef} src={mediaUrl || undefined}
                      poster={session.thumbnailUrl || coverUrl || undefined}
                      playsInline preload="metadata" {...mediaEvents}
                      style={{ maxWidth: '100%', maxHeight: 'min(70vh, 560px)', width: 'auto', height: 'auto', display: 'block' }} />
                  : <>
                      {(() => {
                        const img = session.thumbnailUrl || coverUrl;
                        return img
                          ? <img src={img} alt="" style={{ maxWidth: '100%', maxHeight: 'min(70vh, 560px)', width: 'auto', height: 'auto', display: 'block' }} />
                          : <div style={{ width: '100%', minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #2E6B5E 0%, #1F4E5A 100%)' }}>
                              <svg viewBox="0 0 24 24" width="52" height="52" fill="none" stroke="#9FCAD2" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h3zM3 19a2 2 0 0 0 2 2h1a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1H3z"/>
                              </svg>
                            </div>;
                      })()}
                      <audio ref={mediaRef} src={mediaUrl || undefined} {...mediaEvents} style={{ display: 'none' }} />
                    </>
                }
                {/* Caption line overlay (SW/EN, driven by playback time) */}
                {capText && (
                  <div style={{ position: 'absolute', left: 0, right: 0, bottom: 10, display: 'flex', justifyContent: 'center', padding: '0 10px', pointerEvents: 'none' }}>
                    <span style={{ background: 'rgba(15,35,42,0.82)', color: '#fff', fontSize: 15, lineHeight: 1.4, padding: '6px 12px', borderRadius: 8, maxWidth: '94%', textAlign: 'center', whiteSpace: 'pre-wrap' }}>{capText}</span>
                  </div>
                )}
                {/* Play / pause button overlay */}
                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 60, height: 60, borderRadius: '50%', background: 'rgba(20,50,58,0.82)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                  {playing
                    ? <svg viewBox="0 0 24 24" width="24" height="24" fill="#fff"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>
                    : <svg viewBox="0 0 24 24" width="24" height="24" fill="#fff" style={{ marginLeft: 4 }}><path d="M8 5v14l11-7z"/></svg>
                  }
                </div>
              </div>

              {mediaLoading && !mediaUrl && <div style={{ color: C.muted, fontSize: 12, marginBottom: 10 }}>Loading…</div>}
              {mediaError && <div style={{ color: '#B4544F', fontSize: 12, marginBottom: 10 }}>{mediaError}</div>}

              {/* Seek bar — drag or click anywhere within the listened region */}
              <div ref={seekRef}
                onMouseDown={e => { setDragging(true); seekTo(e.clientX); }}
                onTouchStart={e => { setDragging(true); seekTo(e.touches[0].clientX); }}
                style={{ height: 10, background: '#E3EEF0', borderRadius: 5, cursor: 'pointer', position: 'relative', marginBottom: 6 }}>
                {/* Reached region (seekable) */}
                <div style={{ position: 'absolute', inset: 0, width: `${duration ? (maxReached/duration)*100 : 0}%`, background: C.brown, borderRadius: 5, opacity: 0.3 }} />
                {/* Played region */}
                <div style={{ position: 'absolute', inset: 0, width: `${duration ? (currentTime/duration)*100 : 0}%`, background: C.brown, borderRadius: 5 }} />
                {/* Thumb */}
                {duration > 0 && <div style={{ position: 'absolute', top: '50%', left: `${(currentTime/duration)*100}%`, transform: 'translate(-50%,-50%)', width: 14, height: 14, borderRadius: '50%', background: C.dark, border: `2px solid ${C.brown}`, pointerEvents: 'none' }} />}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: C.muted, marginBottom: capCues?.length ? 10 : 16 }}>
                <span>{fmt(currentTime)}</span>
                <span>{fmt(duration)}</span>
              </div>

              {/* Caption language toggle — only when captions exist */}
              {capCues?.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
                  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke={C.muted} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                    <rect x="2" y="5" width="20" height="14" rx="2" /><path d="M7 12h2M7 15h4M13 12h4M15 15h2" />
                  </svg>
                  {[['orig', 'Original'], ['sw', 'Kiswahili'], ['en', 'English'], ['off', 'Off']]
                    .filter(([l]) => l !== 'orig' || hasOrig)
                    .map(([l, label]) => {
                      const active = effLang === l;
                      return (
                        <button key={l} onClick={() => setLang(l)}
                          style={{ background: active ? C.dark : 'transparent', color: active ? '#fff' : C.muted, border: `1px solid ${active ? C.dark : C.warm}`, borderRadius: 20, padding: '3px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>
                          {label}
                        </button>
                      );
                    })}
                </div>
              )}
            </>
          ) : (
            <p style={{ color: C.muted, fontSize: 14, textAlign: 'center', padding: '24px 0' }}>Maudhui yanakuja hivi karibuni. · Content coming soon.</p>
          )}

          {isComplete && (
            <div style={{ marginTop: 4 }}>
              <JournalEntry sessionId={session.id} userId={userId} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── INTERACTIVE TOOL ─────────────────────────────────────────────────────────
// A calculator / quiz / worksheet built separately — either a hosted page or an
// uploaded self-contained .html. Opens FULL-SCREEN inside the app (never a new tab)
// so the client can always get back to their sessions with one tap.
//
// Security: the frame is sandboxed. Uploaded files get NO `allow-same-origin`, so they
// run in an opaque origin and cannot reach the app's Firebase session (they must keep
// state in memory). External links keep their own origin — normal same-origin policy
// already walls them off from us — so their storage/APIs keep working.
function InteractiveCard({ session, locked }) {
  const [open, setOpen] = useState(false);
  const url = session.interactiveUrl || '';
  const name = session.name || 'Tool';
  const sandbox = session.interactiveSource === 'upload'
    ? 'allow-scripts allow-forms allow-popups'
    : 'allow-scripts allow-same-origin allow-forms allow-popups';

  // While open: lock the page behind, and make the phone's back button close the tool
  // instead of navigating out of the program (what users instinctively press).
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.history.pushState({ tool: session.id }, '');
    const onPop = () => setOpen(false);
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, session.id]);

  // Closing via the back bar unwinds the history entry we pushed, so forward/back stay sane.
  const close = () => { if (window.history.state?.tool === session.id) window.history.back(); else setOpen(false); };

  if (locked) {
    return (
      <div style={{ background: C.surface, borderRadius: 16, marginBottom: 12, border: `1px solid ${C.warm}`, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, opacity: 0.55 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ color: C.muted, fontSize: 15, fontWeight: 500 }}>{name}</div>
          <div style={{ color: C.muted, fontSize: 12, marginTop: 3 }}>Nunua programu hii kuifungua · Purchase this program to unlock</div>
        </div>
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke={C.muted} strokeWidth="1.5" strokeLinecap="round" style={{ flexShrink: 0 }}>
          <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
        </svg>
      </div>
    );
  }

  return (
    <>
      <button onClick={() => url && setOpen(true)} disabled={!url}
        style={{ width: '100%', background: C.surface, borderRadius: 16, marginBottom: 12, border: `1px solid ${C.warm}`, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, cursor: url ? 'pointer' : 'not-allowed', textAlign: 'left', fontFamily: 'Inter, sans-serif' }}>
        <div style={{ width: 52, height: 52, borderRadius: 8, background: '#E8ECF7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#4A5A8A' }}>
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="2" width="16" height="20" rx="2"/><path d="M8 6h8M8 10h3M13 10h3M8 14h3M13 14h3M8 18h8"/>
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: C.dark, fontSize: 15, fontWeight: 500, marginBottom: 2 }}>{name}</div>
          <div style={{ color: C.muted, fontSize: 12, lineHeight: 1.45 }}>
            {session.description || (url ? 'Gusa kufungua · Tap to open' : 'Inakuja hivi karibuni · Coming soon')}
          </div>
        </div>
        {url && <span style={{ color: C.muted, fontSize: 20, flexShrink: 0, paddingLeft: 8 }}>›</span>}
      </button>

      {open && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: C.bg, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: C.surface, borderBottom: `1px solid ${C.warm}`, flexShrink: 0 }}>
            <button onClick={close}
              style={{ background: 'none', border: 'none', color: C.dark, fontSize: 14, cursor: 'pointer', fontFamily: 'Inter, sans-serif', padding: '4px 6px', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
              Rudi kwenye vipindi
            </button>
            <span style={{ color: C.muted, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, textAlign: 'center' }}>{name}</span>
            <a href={url} target="_blank" rel="noopener noreferrer" title="Fungua kwenye dirisha jipya · Open in new tab"
              style={{ color: C.muted, display: 'flex', alignItems: 'center', flexShrink: 0, padding: 4 }}>
              <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><path d="M15 3h6v6M10 14L21 3"/></svg>
            </a>
          </div>
          <iframe title={name} src={url} sandbox={sandbox}
            style={{ flex: 1, width: '100%', border: 'none', background: '#fff' }} />
        </div>
      )}
    </>
  );
}

// ─── REFLECTION BOX ───────────────────────────────────────────────────────────
function ReflectionBox({ session, userId, programId }) {
  const [text, setText] = useState('');
  const [saved, setSaved] = useState(false);
  const [savedText, setSavedText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getDoc(doc(db, 'journals', userId, 'entries', session.id)).then(snap => {
      if (snap.exists()) {
        const t = snap.data().text || '';
        setText(t); setSavedText(t); setSaved(true);
      }
    });
  }, [session.id, userId]);

  const save = async () => {
    setSaving(true);
    await setDoc(doc(db, 'journals', userId, 'entries', session.id), {
      text, programId, type: 'reflection',
      prompt: session.prompt || '',
      updatedAt: new Date().toISOString(),
    }, { merge: true });
    setSavedText(text); setSaved(true); setSaving(false);
  };

  const isDirty = text !== savedText;

  return (
    <div style={{ background: '#EDF2EB', borderRadius: 16, padding: 24, marginBottom: 12, border: '1px solid #B8D4B0' }}>
      <div style={{ color: C.green, fontSize: 12, fontWeight: 600, marginBottom: 10, fontFamily: 'Helvetica,sans-serif', textTransform: 'uppercase', letterSpacing: 0.8 }}>
        Contemplation
      </div>
      {session.prompt && (
        <p style={{ color: C.dark, fontSize: 15, lineHeight: 1.75, margin: '0 0 16px', fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>
          {session.prompt}
        </p>
      )}
      <textarea
        value={text} onChange={e => { setText(e.target.value); setSaved(false); }}
        placeholder="Write your reflection here…" rows={4}
        style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: '1px solid #B8D4B0', fontSize: 14, fontFamily: 'Inter, sans-serif', color: C.text, background: C.surface, boxSizing: 'border-box', resize: 'vertical' }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
        <button onClick={save} disabled={!text.trim() || !isDirty || saving}
          style={{ background: text.trim() && isDirty ? C.green : C.muted, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', cursor: text.trim() && isDirty ? 'pointer' : 'not-allowed', fontSize: 14, fontFamily: 'Inter, sans-serif' }}>
          {saving ? 'Saving…' : saved && !isDirty ? 'Saved ✓' : 'Save'}
        </button>
      </div>
    </div>
  );
}
